<template>
  <v-form
    v-if="inputFields && inputFields.length > 0"
    ref="entityMask"
    key=""
    v-model="isValidForm"
    :disabled="isSaving"
    @submit.prevent=""
  >
    <v-sheet :outlined="sheetOutlined" :color="sheetOutlineColor" rounded>
      <v-card
        :max-width="maxWidth"
        :min-width="maxWidth"
        class="pt-3 px-4"
        flat
        :outlined="cardOutlined"
        :color="cardColor"
      >
        <!-- py-4 restores V2's 16px vertical card-title padding; Vuetify 3's
             .v-card-title shrank it to 8px, tightening the space above/below the
             title (left/right already match V2 at 16px). -->
        <v-card-title class="py-4 emf-dialog-title">
          <h1 class="text-h5 mb-n3" :class="overallTitleDarkness">
            {{ dialogTitlePrefix }} {{ titleSegment0
            }}<span :class="titleSegment1Darkness">{{ title }}</span>
            {{ titleSegment2 }}
          </h1>
        </v-card-title>
        <v-card-title v-if="title2" class="text-h5 py-0 mt-n1 emf-dialog-title">
          <h1 class="text-h5" :class="overallTitleDarkness">
            <span :class="titleSegment1Darkness">{{ title2 }}</span>
          </h1>
        </v-card-title>
        <v-card-subtitle
          v-if="subtitle"
          class="emf-dialog-subtitle"
          style="word-break: break-word"
        >
          <h2
            class="text-subtitle-1 mt-3 mb-n3 ml-1"
            :class="overallSubtitleDarkness"
          >
            {{ subtitle }}
          </h2>
        </v-card-subtitle>
        <v-card-text
          v-if="message && !isSubentityDialog"
          style="word-break: break-word"
          class="mt-3 mb-n9 emf-dialog-message"
          :class="{
            'emf-dialog-message--subdued': doSubdueMessage,
            'emf-dialog-message--aligned': doAlignMessageWithTitle,
          }"
        >
          <div class="text-h6 font-weight-medium mx-4">
            {{ message }}
          </div>
        </v-card-text>
        <v-card-text>
          <!-- min-height: keeps the dialog at the size it opened at when the
               user moves to a shorter tab (see selectMaskTab).
               align-content-start packs the rows at the top of that height; a
               wrapping flex row otherwise STRETCHES its lines to fill it, which
               spread a short tab's fields down the whole dialog. No effect
               without a min-height, so untabbed forms are unchanged. -->
          <v-row
            ref="maskFieldsRow"
            class="my-0 py-0 align-content-start"
            :style="maskFieldsRowStyle"
          >
            <template v-for="(ifield, index) in inputFields">
              <!-- Optional tabs (tabGroups.ts). The bar sits where the spec's
                   FIRST `type: 'tab'` marker is, so fields declared above it stay
                   above it; the markers themselves render nothing. An inactive
                   tab's fields are hidden with v-show on their column, never
                   v-if / `unrendered`: they must stay mounted so VForm still
                   validates them on Save. -->
              <v-col
                v-if="index === tabBarIndex"
                :key="'emf-tabs-' + index"
                cols="12"
                class="py-0 emf-tab-bar"
              >
                <!-- Styled to match the Overview pages' top tabs
                     (components/OverviewPage.vue): fixed-tabs shares the width
                     equally, centered, primary slider, and the same
                     theme-dependent text colour. -->
                <v-tabs
                  :model-value="activeTabKey"
                  fixed-tabs
                  show-arrows
                  centered
                  :color="maskTabTextColor"
                  slider-color="primary"
                  density="compact"
                  @update:model-value="selectMaskTab($event)"
                >
                  <v-tab
                    v-for="maskTab in visibleMaskTabs"
                    :key="maskTab.key"
                    :value="maskTab.key"
                  >
                    {{ maskTab.label }}
                    <v-icon
                      v-if="tabKeysWithErrors.includes(maskTab.key)"
                      color="error"
                      size="small"
                      class="ml-1 emf-tab-error-icon"
                      >mdi-alert-circle</v-icon
                    >
                  </v-tab>
                </v-tabs>
              </v-col>
              <v-col
                v-if="!ifield.unrendered && ifield.type !== 'tab'"
                v-show="isInActiveTab(index)"
                :key="index"
                :cols="ifield.cols"
                class="py-0"
              >
                <div v-if="ifield.type === 'dateField' && !ifield.unrendered">
                  <!-- :disabled on the MENU is what enforces a readonly date.
                       The activator's own `readonly` below is hard-coded for an
                       unrelated reason (it blocks TYPING, so the value can only
                       come from the picker), so binding the spec's flag there
                       would change nothing. Vuetify 3 refuses to open a disabled
                       overlay -- VOverlay's isActive setter is
                       `if (!(v && props.disabled)) model.value = v` -- and
                       closes it if `disabled` flips true while it is open. -->
                  <v-menu
                    ref="menu"
                    v-model="ifield.showPicker"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :close-on-content-click="false"
                    :disabled="doMakeWidgetReadonly(ifield)"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        :ref="'ifield-' + index"
                        v-model="ifield.value"
                        :class="fieldClass(ifield)"
                        :label="ifield.label"
                        :rules="fieldValidations[index]"
                        :disabled="doDisableWidget(ifield, inputFields, index)"
                        prepend-icon="mdi-calendar"
                        readonly
                        :clearable="!doMakeWidgetReadonly(ifield)"
                        v-bind="props"
                        @update:model-value="datePickerChanged({ ifield })"
                      ></v-text-field>
                    </template>
                    <!-- Not `v-model`, and not @change/@click:date. Vuetify 3's
                         VDatePicker emits NEITHER of those V2 events (only
                         update:modelValue), and it models its value as a Date
                         while every dateField spec stores 'YYYY-MM-DD' -- so the
                         old binding both skipped datePickerChanged entirely and
                         wrote a live Date into the field, e.g. a Start Date
                         reading "Wed Jul 15 2026 00:00:00 GMT-0700 (...)".
                         Convert at the boundary instead (see datePickerValue.ts
                         for why this must not go through UTC), then run the
                         normal pipeline. didClickDate reproduces V2's
                         @click:date, which is what closed the menu on a pick. -->
                    <v-date-picker
                      :model-value="isoDateStringToDate(ifield.value)"
                      hide-header
                      @update:model-value="datePickerPicked(ifield, $event)"
                    />
                  </v-menu>
                </div>
                <div v-if="ifield.type === 'timeField' && !ifield.unrendered">
                  <!-- Same readonly gating as the dateField above, and for the
                       same reason. No spec sets `readonly` on a timeField today;
                       this keeps the two picker widgets from drifting apart, and
                       it is what makes view mode actually read-only here. -->
                  <v-menu
                    ref="menu2"
                    v-model="ifield.showPicker"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :close-on-content-click="false"
                    :disabled="doMakeWidgetReadonly(ifield)"
                  >
                    <template #activator="{ props }">
                      <time-v-text-field
                        :ref="'ifield-' + index"
                        v-model="ifield.value"
                        :class="fieldClass(ifield)"
                        :label="ifield.label"
                        :rules="fieldValidations[index]"
                        :disabled="doDisableWidget(ifield, inputFields, index)"
                        prepend-icon="mdi-clock-outline"
                        readonly
                        :clearable="!doMakeWidgetReadonly(ifield)"
                        v-bind="props"
                      ></time-v-text-field>
                    </template>
                    <!-- runOnChanged follows the VALUE, not the clock: V3's
                         @update:hour / @update:minute fire only for a pick on
                         the clock face, and @update:period only when AM/PM
                         actually changes. A time typed into the header's
                         hour / minute boxes emits update:model-value alone, so
                         it never reached runOnChanged -- the hidden datetime
                         stayed as it was, Save stayed disabled, and the menu
                         never closed. Its model stays an 'HH:mm' string, so
                         unlike the date picker no value conversion is needed.
                         A minute picked on the clock still closes the menu,
                         and so does AM / PM once a full time is in (see
                         timePickerClicked). -->
                    <v-time-picker
                      ref="picker"
                      :model-value="ifield.value"
                      scrollable
                      format="ampm"
                      @update:model-value="timePickerChanged(ifield, $event)"
                      @update:minute="ifield.showPicker = false"
                      @click="timePickerClicked(ifield, $event)"
                      @keydown.enter="timePickerEnter(ifield, $event)"
                    />
                  </v-menu>
                </div>
                <div
                  v-if="
                    ifield.type === 'dateTimeDisplayDateOnlyTextField' &&
                    !ifield.unrendered
                  "
                >
                  <v-menu
                    ref="menu3"
                    v-model="ifield.showPicker"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ props }">
                      <date-time-display-date-only-v-text-field
                        :ref="'ifield-' + index"
                        :key="index"
                        v-model="ifield.value"
                        :class="fieldClass(ifield)"
                        :label="ifield.label"
                        :rules="fieldValidations[index]"
                        :disabled="doDisableWidget(ifield, inputFields, index)"
                        prepend-icon="mdi-calendar"
                        readonly
                        clearable
                        v-bind="props"
                      ></date-time-display-date-only-v-text-field>
                    </template>
                    <!-- Same Date-vs-'YYYY-MM-DD' boundary as the dateField
                         picker above. Deliberately does NOT call
                         datePickerChanged: this widget never had a change
                         handler under V2, and its menu closes on content click
                         rather than being closed by one. No input spec declares
                         `dateTimeDisplayDateOnlyTextField` today, so this path
                         is currently unreached -- fixed for correctness so it
                         cannot land a Date in a field if one ever does. -->
                    <v-date-picker
                      :model-value="isoDateStringToDate(ifield.value)"
                      hide-header
                      @update:model-value="
                        ifield.value = dateToIsoDateString($event)
                      "
                    />
                  </v-menu>
                </div>
                <v-card-subtitle
                  v-if="ifield.type === 'subtitle' && !ifield.unrendered"
                  :class="fieldClass(ifield)"
                  class="emf-field-subtitle"
                >
                  {{ ifield.label }}
                </v-card-subtitle>

                <!-- USPS address verification. Display-only and advisory: it
                     never blocks saving, and never edits a field unless the
                     user presses "Use Change". `unrendered` is toggled by the
                     address fields' blur handler, so the panel only exists once
                     there is something to say. -->
                <v-alert
                  v-if="
                    ifield.type === 'addressVerification' && !ifield.unrendered
                  "
                  :type="addressVerificationAlertType(ifield)"
                  :icon="addressVerificationIcon(ifield)"
                  variant="tonal"
                  density="compact"
                  class="mb-2 emf-address-verification"
                >
                  <!-- The sentence gets its OWN full-width row. It used to
                       share a flex row with the buttons, which left it a narrow
                       column and wrapped it mid-phrase ("…a change to / City:")
                       while empty space sat to its right. The address keeps its
                       block shape below, with the buttons bottom-aligned to it.
                       Actions are laid out here rather than through VAlert's
                       `#append` / `#close` slots because those sit in grid areas
                       confined to the FIRST row, so they pin to the top and
                       cannot bottom-align against a multi-line address.
                       `closable` is gone too: "Keep Mine" now IS the dismiss. -->
                  <div class="emf-address-verification__text">
                    {{ ifield.verificationMessage }}
                  </div>
                  <div class="d-flex align-end justify-space-between ga-4 mt-1">
                    <!-- Stacked one line per line of a mailing address, so it
                         reads as an address and lines up against the fields it
                         replaces. Each line is rendered segment by segment so the
                         exact changed words can be marked. -->
                    <div
                      class="emf-address-verification__text font-weight-medium"
                    >
                      <div
                        v-for="(
                          line, lineIndex
                        ) in ifield.verificationSuggestionLines"
                        :key="lineIndex"
                      >
                        <span
                          v-for="(segment, segmentIndex) in line"
                          :key="segmentIndex"
                          :class="
                            segment.changed
                              ? 'emf-address-verification__changed'
                              : ''
                          "
                          >{{ segment.text }}</span
                        >
                      </div>
                    </div>
                    <!-- Decline on the LEFT, apply on the right — the same order
                         as the dialog's own [Cancel] [Save]. -->
                    <div class="d-flex ga-2 flex-shrink-0">
                      <!-- Tonal like its sibling, but backed off on BOTH fill and
                           label — see the CSS note for why both have to move
                           together.
                           Reads "OK" when it stands alone: with no suggestion to
                           weigh against, "Keep Mine" would offer a choice that is
                           not being presented. -->
                      <v-btn
                        size="small"
                        variant="tonal"
                        :rounded="true"
                        class="emf-address-verification__secondary"
                        @click="dismissAddressVerification(ifield)"
                      >
                        {{
                          ifield.verificationSuggestionLines?.length
                            ? 'Keep Mine'
                            : 'OK'
                        }}
                      </v-btn>
                      <!-- Absent when there is nothing to apply, which is the
                           unverifiable case -- there the button above stands
                           alone and reads "OK". -->
                      <v-btn
                        v-if="ifield.verificationSuggestionLines?.length"
                        size="small"
                        variant="tonal"
                        :rounded="true"
                        @click="applyAddressVerification(ifield)"
                      >
                        Use Change
                      </v-btn>
                    </div>
                  </div>
                </v-alert>

                <!-- Duplicate-person "did you mean…?" panel (#113). Display-only,
                     like the address panel above; populated by the identity
                     fields' runOnBlur (cynqueModules/personMatch.ts). -->
                <PersonMatchCandidates
                  v-if="
                    ifield.type === 'personMatchPanel' && !ifield.unrendered
                  "
                  :candidates="ifield.matchCandidates"
                  @select="onPersonMatchSelect"
                />

                <v-spacer v-if="ifield.type === 'spacer'" />

                <v-text-field
                  v-if="ifield.type === 'textField' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  v-no1password="ifield.no1PasswordInteraction"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  :hint="ifield.hint"
                  :clearable="ifield.clearable"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  required
                  @input="textFieldChanged(index)"
                  @blur="
                    ifield.runOnBlur
                      ? ifield.runOnBlur(ifield, inputFields)
                      : undefined
                  "
                />
                <number-v-text-field
                  v-if="ifield.type === 'numberTextField' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @keypress="enforceNumberFormat($event, ifield.value)"
                  @input="updateUnrenderedDisabled(index)"
                />
                <currency-v-text-field
                  v-if="
                    ifield.type === 'currencyTextField' && !ifield.unrendered
                  "
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @keypress="enforceCurrencyFormat($event, ifield.value)"
                  @input="updateUnrenderedDisabled(index)"
                />
                <percentage-v-text-field
                  v-if="
                    ifield.type === 'percentageTextField' && !ifield.unrendered
                  "
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @keypress="enforcePercentageFormat($event, ifield.value)"
                  @input="updateUnrenderedDisabled(index)"
                  @clear-model-value="clearModelValue(ifield)"
                />
                <date-v-text-field
                  v-if="ifield.type === 'dateTextField' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @input="updateUnrenderedDisabled(index)"
                />
                <phone-number-v-text-field
                  v-if="
                    ifield.type === 'phoneNumberTextField' && !ifield.unrendered
                  "
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  v-no1password="ifield.no1PasswordInteraction"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @input="updateUnrenderedDisabled(index)"
                />
                <!-- <date-time-display-date-only-v-text-field
                  v-if="
                    ifield.type === 'dateTimeDisplayDateOnlyTextField' &&
                    !ifield.unrendered
                  "
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :disabled="
                    ifield.disabled !== undefined ? ifield.disabled : false
                  "
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @change="updateUnrenderedDisabled(index)"
                /> -->
                <date-time-display-time-only-v-text-field
                  v-if="
                    ifield.type === 'dateTimeDisplayTimeOnlyTextField' &&
                    !ifield.unrendered
                  "
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  required
                  :clearable="ifield.clearable"
                  :disabled="
                    ifield.disabled !== undefined ? ifield.disabled : false
                  "
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @input="updateUnrenderedDisabled(index)"
                />
                <v-textarea
                  v-if="ifield.type === 'textArea' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :rules="fieldValidations[index]"
                  :hint="ifield.hint"
                  required
                  :variant="ifield.variant"
                  :rows="ifield.rows ?? 1"
                  :auto-grow="ifield.autoGrow ?? true"
                  no-resize
                  :clearable="ifield.clearable"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @change="textAreaChanged(index)"
                >
                  <!-- A fixed-height textarea gets our own handle; the
                       browser's grip is off (`no-resize`). See
                       TextareaResizeHandle.vue. -->
                  <template v-if="ifield.autoGrow === false" #append-inner>
                    <TextareaResizeHandle />
                  </template>
                </v-textarea>
                <!-- The select family listens on @update:model-value, NOT
                     @change. Vuetify 3's VSelect / VAutocomplete / VCombobox
                     declare no `change` emit at all, so `@change` degrades to a
                     NATIVE change listener (Vuetify routes it to the root via
                     its `bubblingEvents` list, where it waits for one to bubble
                     up from the inner input). A select's input is readonly and
                     its value is set programmatically, so the browser never
                     fires one -- which left the entire post-change pipeline
                     dead: clearOtherOnChanged, the unrender/disable passes and
                     runOnChanged all silently never ran. Same class of V2->V3
                     break as the file input further down.
                     `v-model` is declared before this listener in each tag, and
                     Vue merges duplicate onUpdate:modelValue handlers in source
                     order, so ifield.value is already current inside the
                     handler -- exactly the contract @change had. Programmatic
                     writes do not emit, so selectAllItems() and the
                     subentityDidReturnValueValue watcher keep their single
                     explicit call and do not double-fire.
                     Text fields, textareas and checkboxes deliberately KEEP
                     @change/@input: those wrap a real user-edited input, so the
                     native events genuinely fire. -->
                <v-select
                  v-if="ifield.type === 'select' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  v-model="ifield.value"
                  v-no1password="ifield.no1PasswordInteraction"
                  :items="getProcessedItems(ifield)"
                  :class="fieldClass(ifield)"
                  :item-title="selectItemTitle"
                  :item-value="ifield.itemValue || 'id'"
                  :item-props="selectItemProps"
                  :return-object="ifield.doReturnObject"
                  :value-comparator="valueComparatorFor(ifield.itemValue)"
                  :label="ifield.label"
                  :prepend-icon="ifield.prependIcon"
                  :multiple="isFieldMultiple(ifield) ? true : undefined"
                  :chips="ifield.chips || ifield.smallChips ? true : undefined"
                  :hint="ifield.hint"
                  :persistent-hint="ifield.persistentHint"
                  :clearable="ifield.clearable"
                  :rules="fieldValidations[index]"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  :no-data-text="ifield.noDataText"
                  :menu-props="{ offset: -28 }"
                  @focus="
                    ifield.setSelectItems
                      ? runSetSelectItems(ifield)
                      : undefined
                  "
                  @update:model-value="selectChanged(index)"
                  @blur="
                    ifield.runOnBlur
                      ? ifield.runOnBlur(ifield, inputFields)
                      : undefined
                  "
                >
                  <template
                    v-if="
                      doAddNewOptionToSelect(ifield) || doShowSelectAll(ifield)
                    "
                    #prepend-item
                  >
                    <v-list-item
                      v-if="doAddNewOptionToSelect(ifield)"
                      @click="addNewOptionToSelect(index)"
                    >
                      <v-list-item-title>NEW</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="doShowSelectAll(ifield)"
                      :disabled="!isSelectAllEnabled(ifield)"
                      @click="selectAllItems(index)"
                    >
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                  </template>
                  <!-- Vuetify 2's `small-chips` prop does not exist in Vuetify 3,
                       so the old `:small-chips` binding was silently inert and
                       every `smallChips: true` field (the Opportunity/Order
                       "Type" selects, plus the multiple-select "Resource For"
                       and account ones) rendered its selection as plain text.
                       The `:chips` binding above now covers both spec flags, and
                       this slot restores the SMALL size — V3's default chip is
                       bigger than V2's `small` one. The spec API is deliberately
                       unchanged so the six field modules stay identical to
                       main's and keep cherry-picking cleanly.
                       KEEP THE v-if: VSelect treats `hasChips = props.chips ||
                       slots.chip`, so an unconditional slot would turn EVERY
                       select's value into a chip. -->
                  <template
                    v-if="ifield.smallChips"
                    #chip="{ props: chipProps }"
                  >
                    <v-chip v-bind="chipProps" size="small"></v-chip>
                  </template>
                </v-select>
                <!-- A kit's optional items: chips plus a "Choose Options..."
                     window. Same flat value as a multiple select, so it shares
                     selectChanged. -->
                <OptionsChooserField
                  v-if="ifield.type === 'optionsChooser' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  v-model="ifield.value"
                  :items="getProcessedItems(ifield)"
                  :label="ifield.label"
                  :class="fieldClass(ifield)"
                  :rules="fieldValidations[index]"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @update:model-value="selectChanged(index)"
                />
                <v-autocomplete
                  v-if="ifield.type === 'autocomplete' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  v-model="ifield.value"
                  v-no1password="ifield.no1PasswordInteraction"
                  :items="
                    ifield.subentity === 'person'
                      ? getPersonPickerItems(ifield, index)
                      : getProcessedItems(ifield)
                  "
                  :no-filter="ifield.subentity === 'person' || undefined"
                  :class="fieldClass(ifield)"
                  :item-title="selectListItemTitle"
                  :item-value="ifield.itemValue || 'id'"
                  :item-props="selectItemProps"
                  :return-object="ifield.doReturnObject"
                  :value-comparator="valueComparatorFor(ifield.itemValue)"
                  :auto-select-first="true"
                  :label="ifield.label"
                  :multiple="isFieldMultiple(ifield) ? true : undefined"
                  :chips="ifield.chips ? ifield.chips : undefined"
                  :clearable="ifield.clearable"
                  :rules="fieldValidations[index]"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  :no-data-text="autocompleteNoDataText(ifield, index)"
                  @focus="
                    ifield.setSelectItems
                      ? runSetSelectItems(ifield)
                      : undefined
                  "
                  @update:model-value="selectChanged(index)"
                  @update:search="
                    (val) => onAutocompleteSearch(index, ifield, val || '')
                  "
                  @blur="
                    ifield.runOnBlur
                      ? ifield.runOnBlur(ifield, inputFields)
                      : undefined
                  "
                >
                  <template
                    v-if="
                      doAddNewOptionToSelect(ifield) &&
                      (ifield.subentity === 'person'
                        ? !personPickerHasVisibleMatches(ifield, index)
                        : !autocompleteSearchHasMatches(ifield, index))
                    "
                    #prepend-item
                  >
                    <v-list-item @click="addNewOptionToSelect(index)">
                      <v-list-item-title>NEW</v-list-item-title>
                    </v-list-item>
                    <v-divider />
                  </template>
                  <!-- A chip shows the item's LABEL even when the list shows
                       more (`_listTitle`: a location's nickname plus its
                       address). Without the slot Vuetify titles both from
                       item-title. KEEP THE v-if: VAutocomplete treats
                       `hasChips = props.chips || slots.chip`, so an
                       unconditional slot would chip every autocomplete. -->
                  <template
                    v-if="ifield.chips"
                    #chip="{ props: chipProps, item: chipItem }"
                  >
                    <v-chip
                      v-bind="chipProps"
                      :text="selectItemTitle(chipItem.raw)"
                      :disabled="chipItem.props.disabled"
                    />
                  </template>
                </v-autocomplete>
                <!--
                  NOTE: return-object is NOT bound from `doReturnObject` here.
                  VCombobox defaults returnObject to true (unlike
                  v-select/v-autocomplete), and every combobox relies on that
                  default: a typed entry is a string, a picked one the item.
                  A list field (`delimitedStringSeparator`) or a spec that sets
                  `itemValue` opts in to comboboxItemBindings: closable chips,
                  typed delimiters. With `itemValue` the list shows the item's
                  label, while its chips show -- and the save stores -- only
                  that property of each entry (e.g. a contact's email;
                  delimitedList.ts). The picked ROW stays in the form so other
                  fields can follow it. Every other combobox gets no extra
                  bindings at all.
                -->
                <v-combobox
                  v-if="ifield.type === 'combobox' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  :key="index"
                  v-model="ifield.value"
                  v-no1password="ifield.no1PasswordInteraction"
                  :items="getProcessedItems(ifield)"
                  :item-props="selectItemProps"
                  :class="fieldClass(ifield)"
                  :label="ifield.label"
                  :hint="ifield.hint"
                  :multiple="isFieldMultiple(ifield) ? true : undefined"
                  :chips="ifield.chips ? ifield.chips : undefined"
                  :clearable="ifield.clearable"
                  :rules="fieldValidations[index]"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  :no-data-text="ifield.noDataText"
                  :no-filter="ifield.doNotFilterItems === true"
                  :loading="ifield.isLoadingItems === true"
                  v-bind="comboboxItemBindings(ifield, index)"
                  @focus="
                    ifield.setSelectItems
                      ? runSetSelectItems(ifield)
                      : undefined
                  "
                  @update:model-value="comboboxChanged(index)"
                  @update:search="(val) => onComboboxSearch(index, val || '')"
                  @blur="
                    ifield.runOnBlur
                      ? ifield.runOnBlur(ifield, inputFields)
                      : undefined
                  "
                >
                  <!-- The chip shows the STORED value (the address), while
                       the list shows the label (namePlusBiz). Without the
                       slot Vuetify titles both from item-title. `props`
                       carries the close button and its key handling.
                       onMousedown is REPLACED (not added to -- v-bind would
                       merge the two): see onComboboxChipMousedown. -->
                  <template
                    v-if="hasComboboxItemBindings(ifield)"
                    #chip="{ props: chipProps, item: chipItem }"
                  >
                    <v-chip
                      v-bind="{
                        ...chipProps,
                        onMousedown: onComboboxChipMousedown,
                      }"
                      :text="String(chipItem.value)"
                    />
                  </template>
                </v-combobox>
                <v-checkbox
                  v-if="ifield.type === 'checkbox' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  v-model="ifield.value"
                  :label="ifield.label"
                  :class="fieldClass(ifield)"
                  :rules="fieldValidations[index]"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @change="checkboxChanged(index)"
                ></v-checkbox>
                <!-- `:thumb-size="24"` was dropped here on purpose: in V2 that
                     prop sized the thumb LABEL (the dot itself was a fixed 12px
                     in Vuetify's own CSS), while in V3 it sizes the THUMB, so
                     carrying the number across doubled the dot. V3's thumb size
                     comes from the `VSlider` defaults in nuxt.config.ts (12px,
                     V2's dot); the label is sized in the style block below. -->
                <v-slider
                  v-if="ifield.type === 'slider' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  v-model="ifield.value"
                  :label="ifield.label"
                  :class="fieldClass(ifield)"
                  :rules="fieldValidations[index]"
                  thumb-label="always"
                  :hint="ifield.hint"
                  :min="ifield.min"
                  :max="ifield.max"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                ></v-slider>
                <!-- `small-chips` below was V2-only and dead in V3 (same trap as
                     the v-select above), so selected filenames rendered as a plain
                     comma-joined string. Unlike VSelect this needs no #chip slot:
                     given `chips`, VFileInput builds its own VChip at
                     `size: "small"` (VFileInput.js:289) — already the V2 look. -->
                <v-file-input
                  v-if="ifield.type === 'fileSelect' && !ifield.unrendered"
                  :ref="'ifield-' + index"
                  :label="ifield.label"
                  :class="fieldClass(ifield)"
                  :rules="fieldValidations[index]"
                  :accept="ifield.accept"
                  chips
                  :prepend-icon="ifield.prependIcon"
                  :multiple="
                    ifield.multiple !== undefined ? ifield.multiple : false
                  "
                  show-size
                  counter
                  :hint="ifield.hint"
                  :persistent-hint="ifield.persistentHint"
                  :disabled="doDisableWidget(ifield, inputFields, index)"
                  :readonly="
                    isViewMode ||
                    (ifield.readonly !== undefined ? ifield.readonly : false)
                  "
                  @update:model-value="filesChanged($event, index)"
                  @mousedown.capture="suppressFileInputPrependIcon"
                  @click.capture="suppressFileInputPrependIcon"
                ></v-file-input>
                <v-divider
                  v-if="ifield.type === 'divider' && !ifield.unrendered"
                  :color="dividerColor"
                  class="emf-field-divider"
                  :class="fieldClass(ifield)"
                />
              </v-col>
            </template>
          </v-row>
        </v-card-text>
        <!-- Vuetify 3's <v-card-actions> provideDefaults its buttons to
             `variant: text` (flat), where V2 rendered them elevated (raised, with
             a background + shadow). The explicit `variant="elevated"` on each
             button below overrides that so the dialog's buttons match V2. This is
             scoped to these buttons only -- it does NOT touch the TableCard row
             buttons (kebab/disclosure/hoverable), which set their own variant. -->
        <v-card-actions>
          <v-icon
            v-if="isViewMode"
            :color="lockIconColor"
            class="mt-2 ml-8"
            @click="goEditMode()"
            >mdi-lock</v-icon
          >
          <v-spacer />
          <v-icon
            v-if="
              doDisplayValidationCheckmarkOnValidation &&
              showIsValidatedCheckmark
            "
            large
            color="green"
            class="mt-2"
            >mdi-check-bold</v-icon
          >
          <v-spacer />
          <v-btn
            ref="btn"
            :disabled="isSaving"
            :rounded="true"
            variant="elevated"
            :color="cancelButtonColor"
            class="mt-8 mb-5 mr-3 text-high-emphasis"
            :width="calculatedCancelButtonWidth"
            @click="cancel"
          >
            {{ cancelButtonLabel }}
          </v-btn>
          <v-btn
            v-if="isViewMode"
            ref="btn0"
            :disabled="disableEditButton"
            :rounded="true"
            variant="elevated"
            width="200"
            class="mt-8 mb-5"
            color="primary"
            type="submit.prevent"
            @click="goEditMode()"
          >
            Edit
          </v-btn>
          <v-btn
            v-if="!isViewMode"
            ref="btn1"
            :disabled="!isDirty || isSaving"
            :loading="isSaving && !saveAndAddAnotherClicked"
            :rounded="true"
            variant="elevated"
            :width="calculatedSaveButtonWidth"
            class="mt-8 mb-5"
            :class="saveButtonClassModifier"
            :color="saveButtonColor"
            type="submit.prevent"
            @click="trySave({ wasClickedSaveAndAddAnother: false }, $event)"
          >
            {{ saveButtonLabelToUse }}
          </v-btn>
          <v-btn
            v-if="allowAddAnother && !isViewMode"
            ref="btn2"
            :disabled="!isDirty || isSaving"
            :loading="isSaving && saveAndAddAnotherClicked"
            :rounded="true"
            variant="elevated"
            width="220"
            class="mt-8 mb-5"
            color="primary"
            type="submit.prevent"
            @click="trySave({ wasClickedSaveAndAddAnother: true }, $event)"
          >
            Save / Add Another
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-sheet>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, markRaw, PropType } from 'vue'
import { capitalize, cloneDeep, debounce } from 'lodash'
import type { DebouncedFunc } from 'lodash'
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
/* eslint-disable no-console */
import type {
  IEntityMaskData,
  TEntityMaskItem,
  IEntityParentObjectForCreate,
  TEntityMaskDataValueTypes,
} from '~/types/models'
import type { IValueToPassToSubentityDialog } from '~/cynqueModules/setShowSubentityDialog'
import {
  assertIsAnEntityMaskData,
  assertIsDefined,
  assertIsArray,
  assertIsNotUndefined,
  isDisabledSelectItem,
  isEntityMaskData,
} from '~/types/models'
import { enforceNumberFormat } from '~/components/NumberVTextField'
import { enforceCurrencyFormat } from '~/components/CurrencyVTextField'
import { enforcePercentageFormat } from '~/components/PercentageVTextField'
import { setShowSubentityDialog } from '~/cynqueModules/setShowSubentityDialog'
import {
  linkExistingPersonToParent,
  personNamePrefillValues,
  matchPersonIdsBySearch,
} from '~/cynqueModules/personMatch'
import {
  getClearFieldValue,
  getClearFieldValueFromStart,
} from '~/cynqueModules/getClearFieldValues'
import { presetUnrenderedStatesForEditing } from '~/cynqueModules/presetUnrenderedStatesForEditing'
import { getInputFieldDefaultValue } from '~/cynqueModules/getInputFieldDefaultValue'
// Pure logic extracted from this component into unit-tested helpers (Nuxt 3
// migration prep — mirrors the TableCard refactor). The methods/computeds below
// forward to these; see cynqueModules/componentHelpers/entityMaskForm/.
import {
  filterToEntityMaskData,
  isDirtyIField,
  isFormDirty,
  getDirtyFieldsObject,
  compensateForClearableClearSettingWrongFalseyValue,
} from '~/cynqueModules/componentHelpers/entityMaskForm/dirtyFields'
import { buildFieldValidations } from '~/cynqueModules/componentHelpers/entityMaskForm/fieldValidations'
import { parseEntityMaskTitle } from '~/cynqueModules/componentHelpers/entityMaskForm/maskTitle'
import type { IParsedEntityMaskTitle } from '~/cynqueModules/componentHelpers/entityMaskForm/maskTitle'
import { getFieldClass } from '~/cynqueModules/componentHelpers/entityMaskForm/fieldClass'
import {
  doDisableWidget,
  doMakeWidgetReadonly,
  autocompleteSearchHasMatches,
  doAddNewOptionToSelect,
  isFieldMultiple,
  doShowSelectAll,
  isSelectAllEnabled,
  shouldCloseMenuOnAllSelected,
} from '~/cynqueModules/componentHelpers/entityMaskForm/selectFieldRules'
import { hasAValue } from '~/cynqueModules/componentHelpers/entityMaskForm/hasAValue'
import { resolveSaveButtonLabel } from '~/cynqueModules/componentHelpers/entityMaskForm/saveButtonLabel'
import {
  buildTabLayout,
  getVisibleTabs,
  resolveActiveTabKey,
  isIndexInActiveTab,
  getTabKeysWithErrors,
  resolveTabForErrors,
} from '~/cynqueModules/componentHelpers/entityMaskForm/tabGroups'
import type {
  IMaskTab,
  IMaskTabLayout,
} from '~/cynqueModules/componentHelpers/entityMaskForm/tabGroups'
import { selectItemProps } from '~/cynqueModules/componentHelpers/entityMaskForm/selectItemProps'
import { isDelimitedListField } from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'
import {
  isoDateStringToDate,
  dateToIsoDateString,
} from '~/cynqueModules/componentHelpers/entityMaskForm/datePickerValue'
import PhoneNumberVTextField from '~/components/PhoneNumberVTextField'
import { useEntityDialogStore } from '~/stores/entityDialog'

const DEFAULT_RUN_ON_SEARCH_DEBOUNCE_MS = 250

const mn = '[components/EntityMaskForm.vue] |'

// Match return-object select/autocomplete models to their items by the item-value key
// rather than Vuetify 3's default deepEqual comparator. setItemsForSelect augments each
// item with a `label` property the stored model object does not carry, so deepEqual never
// matches the selection to an item and the field renders "[object Object]". Cached per key
// so the bound prop stays a stable reference across renders.
const selectValueComparators: Record<string, (a: any, b: any) => boolean> = {}
function getSelectValueComparator(itemValueKey?: string) {
  const key = itemValueKey || 'id'
  if (!selectValueComparators[key]) {
    selectValueComparators[key] = (a: any, b: any) => {
      // Vuetify calls this as valueComparator(modelValue, item.value). For a return-object
      // field the model is the full object while item.value is the item-value (e.g. the id),
      // so reduce whichever side is an object to its key before comparing.
      const av = a && typeof a === 'object' ? a[key] : a
      const bv = b && typeof b === 'object' ? b[key] : b
      return av === bv
    }
  }
  return selectValueComparators[key]
}

// Place `data-1p-ignore` on a field's inner <input>/<textarea> (the `v-no1password`
// directive below). Vuetify 2 let the `:data-1p-ignore` fall-through attr land on the
// <input>, so 1Password suppressed its icon/autofill dropdown there. Vuetify 3 instead
// applies fall-through attrs to the component ROOT (`.v-input` wrapper), where 1Password
// never looks -- so the `no1PasswordInteraction` field-spec flag silently stopped working
// for EVERY field that sets it (name, address lines, city, ...). Set it on the input
// element directly to restore V2 behaviour.
function applyNo1PasswordIgnore(el: HTMLElement, binding: { value: unknown }) {
  const field = el.querySelector('input, textarea')
  if (!field) {
    return
  }
  if (binding.value) {
    field.setAttribute('data-1p-ignore', 'true')
  } else {
    field.removeAttribute('data-1p-ignore')
  }
}
const no1PasswordDirective = {
  mounted: applyNo1PasswordIgnore,
  updated: applyNo1PasswordIgnore,
}

export default defineComponent({
  directives: {
    no1password: no1PasswordDirective,
  },
  props: {
    startInViewMode: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    disableEditButton: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    suppressFocusOnFirstField: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    entityNameTitlePrefixOverride: {
      type: String as PropType<string | null>,
      default: null,
    },
    entityNameTitleOverride: {
      type: String as PropType<string | null>,
      default: null,
    },
    subtitle: {
      type: String as PropType<string | null>,
      default: null,
    },
    message: {
      type: String as PropType<string | null>,
      default: null,
    },
    // Show `message` as a quieter instruction under the title -- regular
    // weight and a step lighter -- rather than a second heading. Used by the
    // kit options dialogs and the options chooser.
    doSubdueMessage: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    // Start `message` at the title's and the fields' left edge, rather than
    // 16px in. Used by the options chooser and the Send Email dialogs -- NOT
    // the kit options dialogs, which subdue their message but keep the inset.
    doAlignMessageWithTitle: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    allowAddAnother: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    entityName: {
      type: String as PropType<string | null>,
      default: null,
    },
    entityData: {
      type: Object as PropType<object | undefined>,
      default: undefined,
    },
    entityParentObjectForCreate: {
      type: Object as PropType<IEntityParentObjectForCreate | undefined>,
      default: undefined,
    },
    saveButtonCaptionVerbOverride: {
      type: String as PropType<string | null>,
      default: null,
    },
    inputFields: {
      type: Array as PropType<TEntityMaskItem[]>,
      required: true,
    },
    maxWidth: {
      type: Number as PropType<number>,
      required: true,
    },
    isSaving: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isSubentityDialog: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    valuesForSubentityFieldsWhoseValuesToSetFromEntityFields: {
      type: Array as PropType<IValueToPassToSubentityDialog[] | undefined>,
      default: undefined,
    },
    subentityDidCancel: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    subentityDidReturnValueValue: {
      type: Object,
      default: undefined,
    },
    cancelButtonLabel: {
      type: String as PropType<string>,
      default: 'Cancel',
    },
    saveButtonLabel: {
      type: String as PropType<string>,
      default: 'Save',
    },
    saveButtonWidth: {
      type: Number as PropType<number>,
      default: 150,
    },
    cancelButtonWidth: {
      type: Number as PropType<number>,
      default: 150,
    },
    doTintAndDarkenOutlineForLightTheme: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    doDisplayValidationCheckmarkOnValidation: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  emits: ['cancel', 'save'],
  data: () => ({
    dialogTitlePrefix: '{Prefix not set}',
    fieldValidations: [] as unknown as Function[][],
    isValidForm: false,
    showIsValidatedCheckmark: false,
    saveAndAddAnotherClicked: false,
    index: undefined as unknown as number,
    isViewMode: true,
    autocompleteSearchTexts: {} as Record<number, string>,
    // Per-field debouncers for runOnSearch, keyed by field index. markRaw
    // because these are lodash function objects carrying their own timer state
    // — there is nothing for Vue to track, and proxying a function object is
    // the sort of thing that has bitten this component before (see the
    // markRaw'd hover components elsewhere in the app).
    searchDebouncers: markRaw(
      {} as Record<number, DebouncedFunc<(searchText: string) => void>>
    ),
    // Fuzzy person-picker matches (#113). Per person-autocomplete field index:
    // the set of person ids the server matched for the current search, so the
    // picker can surface near-misses ("Rob" -> "Robert Smith") the client-side
    // substring filter would hide. The debouncers are markRaw like the ones
    // above (function objects with their own timer state, nothing to track).
    personFuzzyMatchIds: {} as Record<number, Set<string>>,
    personFuzzyDebouncers: markRaw(
      {} as Record<number, DebouncedFunc<(searchText: string) => void>>
    ),
    // The selected tab's key (its marker's `name`), or null for a mask without
    // tabs. See tabGroups.ts.
    activeTabKey: null as string | null,
    // The fields area's height, in px, as it stood the first time this opening
    // left its initial tab; null until then. Applied as a min-height so a
    // shorter tab does not shrink the dialog (and jump it, since the dialog is
    // centred). A TALLER tab still grows it -- nothing is clipped or scrolled.
    maskFieldsMinHeight: null as number | null,
  }),
  computed: {
    hasInputFields(): boolean {
      return this.inputFields && this.inputFields.length > 0
    },
    inputFieldsOnlyIEntityMaskData(): IEntityMaskData[] {
      return filterToEntityMaskData(this.inputFields)
    },
    maskTabLayout(): IMaskTabLayout {
      return buildTabLayout(this.inputFields)
    },
    visibleMaskTabs(): IMaskTab[] {
      return getVisibleTabs(this.maskTabLayout, this.inputFields)
    },
    // Where the tab bar renders: the first marker's slot, or -1 (no bar) when
    // the mask has no tabs or none of them has anything to show.
    tabBarIndex(): number {
      if (this.visibleMaskTabs.length === 0) {
        return -1
      }
      return this.maskTabLayout.tabKeyByIndex.findIndex((key) => key !== null)
    },
    maskTabTextColor(): string {
      return isDarkTheme() ? 'secondary' : 'primary'
    },
    maskFieldsRowStyle(): Record<string, string> {
      return this.maskFieldsMinHeight === null
        ? {}
        : { minHeight: `${this.maskFieldsMinHeight}px` }
    },
    tabKeysWithErrors(): (string | null)[] {
      return getTabKeysWithErrors({
        layout: this.maskTabLayout,
        inputFields: this.inputFields,
        fieldValidations: this.fieldValidations,
      })
    },
    isDirty(): boolean {
      const fn = `${mn} isDirty |`
      const state = isFormDirty(this.inputFields, this.isEditing)
      console.debug(`${fn} isDirty: ${state}`)
      if (state) {
        console.debug(
          `${fn} isDirtyArray: `,
          this.inputFields.filter((ifield) => this.isDirtyIField(ifield))
        )
      }
      return state
    },
    isEditing(): boolean {
      return !!this.entityData && !this.isSubentityDialog // subentitydialog is always creating.
    },
    // All title-string parsing lives in the pure maskTitle helper; these
    // computeds just read off the single parsed object.
    parsedTitle(): IParsedEntityMaskTitle {
      return parseEntityMaskTitle(this.entityNameTitleOverride, this.entityName)
    },
    title1(): string | null | undefined {
      return this.parsedTitle.title1
    },
    title2(): string | undefined {
      return this.parsedTitle.title2
    },
    title(): string {
      return this.parsedTitle.title
    },
    titleSegment0(): string {
      return this.parsedTitle.titleSegment0
    },
    titleSegment2(): string {
      return this.parsedTitle.titleSegment2
    },
    overallTitleDarkness(): string {
      return isDarkTheme()
        ? 'text-primary-lighten-1'
        : 'font-weight-medium text-primary-lighten-1'
    },
    titleSegment1Darkness(): string {
      return isDarkTheme()
        ? 'text-primary-lighten-2'
        : 'font-weight-medium text-primary-darken-1'
    },
    overallSubtitleDarkness(): string {
      return isDarkTheme()
        ? 'text-primary'
        : 'font-weight-medium text-primary-lighten-1'
    },
    entityNameCapitalized(): string {
      return this.parsedTitle.entityNameCapitalized
    },
    calculatedCancelButtonWidth(): number {
      return this.allowAddAnother ? 110 : this.cancelButtonWidth
    },
    calculatedSaveButtonWidth(): number {
      return this.allowAddAnother ? 110 : this.saveButtonWidth
    },
    saveButtonColor(): string | undefined {
      return this.allowAddAnother ? undefined : 'primary'
    },
    saveButtonClassModifier(): string {
      return this.allowAddAnother ? 'mr-3' : ''
    },
    saveButtonLabelToUse(): string {
      return resolveSaveButtonLabel({
        inputFields: this.inputFields,
        isEditing: this.isEditing,
        override: this.saveButtonCaptionVerbOverride,
        defaultLabel: this.saveButtonLabel,
      })
    },
    saveButtonCaptionVerbUpperCase(): string {
      return this.saveButtonLabelToUse.toUpperCase()
    },
    sheetOutlined(): boolean {
      return this.doTintAndDarkenOutlineForLightTheme && !isDarkTheme()
    },
    cardOutlined(): boolean {
      return (
        (!this.doTintAndDarkenOutlineForLightTheme && !isDarkTheme()) ||
        isDarkTheme()
      )
    },
    sheetOutlineColor(): string | undefined {
      return this.doTintAndDarkenOutlineForLightTheme && !isDarkTheme()
        ? 'grey'
        : undefined
    },
    // Bridge's Cancel button took the framework's default button fill, which is
    // theme-aware: #F5F5F5 in light but #272727 (with white text) in dark. The V3
    // port hard-coded `color="grey-lighten-4"`, so in dark mode the button stayed
    // near-white and read as the loudest thing in the dialog. Measured on Bridge:
    // light rgb(245,245,245), dark rgb(39,39,39). V3 derives the label colour from
    // the background, so naming the fill restores the white label too.
    cancelButtonColor(): string {
      return isDarkTheme() ? '#272727' : 'grey-lighten-4'
    },
    cardColor(): string | undefined {
      // Dark mode: lift the dialog slightly off the page behind it. Before this,
      // the card fell through to the theme `surface` (#1E1E1E since the dark
      // elevation-overlay work), which is the same value the cards on the page
      // underneath use — so the dialog read as flat against them. `#333` is not a
      // new shade: EntityDeleteForm in this same dialog family already uses it, so
      // the two now agree instead of differing.
      // Light mode is unchanged.
      if (isDarkTheme()) {
        return '#333'
      }
      return this.doTintAndDarkenOutlineForLightTheme
        ? 'grey-lighten-5'
        : undefined
    },
    lockIconColor(): string {
      return !isDarkTheme() ? 'secondary-lighten-1' : 'secondary'
    },
    dividerColor(): string {
      return !isDarkTheme() ? '#AB47BC' : '#8E24AA'
      //  Known Vuetify BUG (fixed in a 2.x version we haven't installed)...
      //  Vuetify colors not working for v-divider  JR.
      // return isDarkTheme() ? 'primary-darken-1' : 'primary-lighten-1'
    },
  },
  watch: {
    inputFields: {
      immediate: true,
      async handler(newInputFieldsArray) {
        const fn = `${mn} watch inputFields |`
        console.debug(`${fn} fired`)
        // The debouncers are keyed by index into the OLD array. Whether the
        // dialog is closing or re-opening on a different entity, a pending
        // timer now points at the wrong field, so drop them all first.
        this.cancelSearchDebouncers()
        if (!newInputFieldsArray || newInputFieldsArray.length < 1) {
          console.debug(`${fn} just cleared inputFields. Done for now.`)
          return
        }
        this.isViewMode = this.startInViewMode
        this.setStartingDialogTitlePrefix()
        this.setSubentityValuesFromEntity()
        if (!this.isEditing) {
          await this.setDefaultValues()
        }
        this.runOnOpen()
        this.clearValidationsArray()
        this.clearShowIsValidatedCheckmark()
        this.clearSaveAndAddAnotherClickedFlag()
        this.setValuesForDisplayOnly()
        this.presetUnrenderedStateForEditing()
        this.renderUnrenderInputFields()
        this.enableDisableInputFields()
        // Every opening starts on the first tab -- including the re-open after
        // Save / Add Another, which replaces inputFields. Set before
        // focusOnFirstField, which only looks on the active tab.
        this.maskFieldsMinHeight = null
        this.activeTabKey = resolveActiveTabKey(null, this.visibleMaskTabs)
        this.focusOnFirstField()
      },
    },
    //
    // Note: The EntityDialog and EntityMaskForm components can
    // be used to get data for both entity and subentity--for example,
    // for entity 'Company' and subentity 'Contact' of 'Company'.
    // The two EntityDialog instances nest.
    subentityDidCancel(value) {
      const fn = `${mn} subentityDidCancel |`
      console.debug(`${fn} changed to ${value}`)
      if (!value) {
        return
      }

      // this.inputFields[this.index].value = null
      useEntityDialogStore().setSubentityReturnValue(null)
    },
    async subentityDidReturnValueValue(value) {
      const fn = `${mn} subentityDidReturnValueValue |`
      console.debug(`${fn}`, value)

      if (value === undefined) {
        console.debug(
          `${fn} subentityDidReturnValueValueValue is undefined.`,
          'Aborting adding (non-existent) new value.'
        )
        return
      }

      // Update all selection options lists for this inputField's subentity
      const thisIfield = this.inputFields[this.index]
      assertIsAnEntityMaskData(fn, thisIfield) // guard; should always be
      const subentity = thisIfield.subentity
      console.debug(
        `${fn} this.index = ${this.index}; subentity = '${subentity}'`
      )

      // for (const ifield of this.inputfields) {
      //   if (ifield.subentity === subentity && ifield.items !== undefined) {
      //     console.debug(`${fn} calling runSetSelectItems for '${ifield.name}'`)
      //     this.runSetSelectItems(ifield)
      //     console.debug(`${fn} done for '${ifield.name}'`)
      //   }
      // }

      // 1. Set value FIRST (synchronously) so any rendering evaluations
      //    during the async items refresh see the correct value
      if (thisIfield.multiple) {
        if (!thisIfield.value) {
          thisIfield.value = [value]
        } else {
          assertIsArray(fn, thisIfield.value)
          thisIfield.value.push(value)
        }
      } else {
        thisIfield.value = value
      }

      console.debug(
        `${fn} this.inputFields[this.index].value set to`,
        thisIfield.value
      )

      // 2. Refresh items lists (use allSettled so a single API failure
      //    doesn't prevent selectChanged from running)
      await Promise.allSettled(
        this.inputFieldsOnlyIEntityMaskData
          .filter(
            (ifield) =>
              ifield.subentity === subentity && ifield.items !== undefined
          )
          .map((ifield) => {
            console.debug(
              `${fn} calling runSetSelectItems for '${ifield.name}'`
            )
            return this.runSetSelectItems(ifield)
          })
      )

      console.debug(`${fn} With new item added, 'items' is now...`)
      console.debug(thisIfield.items)

      // 3. For autocomplete fields with doReturnObject: update value to use
      //    the matching item from the refreshed items list, so Vuetify's
      //    deep-equality check can match the value against the items array
      if (
        thisIfield.type === 'autocomplete' &&
        thisIfield.doReturnObject &&
        thisIfield.items
      ) {
        if (thisIfield.multiple && Array.isArray(thisIfield.value)) {
          const idx = thisIfield.value.findIndex((v) => v.id === value.id)
          if (idx !== -1) {
            const match = thisIfield.items.find((i) => i.id === value.id)
            if (match) {
              thisIfield.value.splice(idx, 1, match)
            }
          }
        } else if (!thisIfield.multiple) {
          const match = thisIfield.items.find((i) => i.id === value.id)
          if (match) {
            thisIfield.value = match
          }
        }
      }

      this.selectChanged(this.index)

      if (thisIfield.runOnChanged) {
        console.debug(`${fn} has runOnChanged function. Running it.`)
        const that = this // Sometimes need access to things like the store
        thisIfield.runOnChanged(
          thisIfield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
      }

      // ...and runOnBlur, which is where a field installs the state its
      // DEPENDENT fields need now that its own value has settled — every
      // runOnBlur on a SELECT does exactly that, and only that:
      // (Since 2026-08-13 runOnBlur is also bound on textField and combobox, so
      // that the address fields can trigger USPS verification. Before that it
      // was wired on select/autocomplete ONLY, which made it silently inert on
      // every other widget despite the name. Nothing else declared it on those
      // types, so widening activated no dormant code.)
      //   targetIField.subentityParentObjectForCreate = () => ({ ... })
      //
      // Setting this field's value from a returned subentity never fires a real
      // blur, so that state was silently never installed. Concretely: create a
      // Company, create an Industry inline via NEW, then create a Subindustry
      // inline — the subindustry was POSTed with NO `industry`, because
      // subindustry's subentityParentObjectForCreate is only ever set by
      // industry's runOnBlur. That malformed record then broke the subindustry
      // list for good (company.ts dereferences `item.industry.id`).
      //
      // Same shape applies to every parent/child select pair that leans on
      // runOnBlur — productGroup->product, supplier->tsdSupplier, and the rest.
      // Ordering mirrors a real interaction: changed, then blurred.
      if (thisIfield.runOnBlur) {
        console.debug(`${fn} has runOnBlur function. Running it.`)
        await thisIfield.runOnBlur(thisIfield, this.inputFields)
      }

      // Clear stored newly created subentity intance
      useEntityDialogStore().setSubentityReturnValue(null)

      // Blur the autocomplete to force Vuetify to display the selected
      // value (clears internal isFocused/lazySearch state)
      if (thisIfield.type === 'autocomplete') {
        this.$nextTick(() => {
          const refs = this.$refs['ifield-' + this.index]
          if (refs && refs[0]) {
            refs[0].blur()
          }
        })
      }

      // Close the dropdown the "NEW" item was clicked in.
      //
      // Vuetify 2 closed it as a side effect of the list-item click. Vuetify 3
      // does not: "NEW" lives in the `prepend-item` slot, so clicking it selects
      // nothing, and nothing tells the menu to close — it was left hanging open
      // over the form once the subentity dialog closed.
      //
      // V3 exposes the menu as a writable boolean on the component ref (setting
      // it false closes it; verified in-browser). V2 has no such property — its
      // equivalent is the internal `isMenuActive`, which `blur()` clears — so
      // the type guard makes this a no-op there and one file still serves both
      // worktrees, the same way getSelectList emits both item shapes.
      this.$nextTick(() => {
        // `as any`: $refs is typed Element | Vue, and neither declares the
        // Vuetify-3-only `menu`. The duck-type check below is the real guard.
        const refs = this.$refs['ifield-' + this.index] as any
        const field = Array.isArray(refs) ? refs[0] : refs
        if (field && typeof field.menu === 'boolean') {
          field.menu = false
          // Close the menu but KEEP the field focused, so the user can tab
          // straight on to the next field. Closing the overlay otherwise drops
          // focus to <body> on V3 — measured — and the next Tab jumps out of
          // the form entirely (to a nav link). Focusing does not re-open the
          // menu: V3 opens it on click/keydown, not on focus.
          if (typeof field.focus === 'function') {
            field.focus()
          }
        }
      })
    },
    isDirty(newValue) {
      if (newValue) {
        this.showIsValidatedCheckmark = false
      }
    },
    // A runOnChanged can empty the tab the user is on (every field in it
    // unrendered); its header then disappears, so move off it.
    visibleMaskTabs(newVisibleTabs: IMaskTab[]) {
      this.activeTabKey = resolveActiveTabKey(this.activeTabKey, newVisibleTabs)
    },
  },
  // A debounced search that fires after the dialog closes would read
  // this.inputFields[index] on an array EntityDialog has already reset to [],
  // and throw. Same shape as the ListPage-callback-outlives-its-page bug.
  beforeUnmount() {
    this.cancelSearchDebouncers()
  },
  methods: {
    // Stable per-key value-comparator so return-object selections match their
    // (label-augmented) items by item-value — see getSelectValueComparator above.
    valueComparatorFor(itemValueKey?: string) {
      return getSelectValueComparator(itemValueKey)
    },
    // Resolve a select/autocomplete option's display title. Loaded items carry a `label`
    // (setItemsForSelect adds it); a pre-set / default return-object value (e.g. Country's
    // raw {id, name}, which lacks `label` and isn't yet among the lazily-loaded items) may
    // only carry its source property, so fall back to `name` so the field shows its value
    // rather than "[object Object]".
    selectItemTitle(item: any): string {
      if (item == null) {
        return ''
      }
      if (typeof item !== 'object') {
        return String(item)
      }
      return item.label ?? item.name ?? ''
    },
    // An autocomplete's LIST title: `_listTitle` where the loader gave one
    // (the Add Services dialogs' Location(s): nickname + address, which the
    // search then matches too), else the label. Chips keep the label -- see
    // the #chip slot. A bare method for the same identity reason as below.
    selectListItemTitle(item: any): string {
      if (item && typeof item === 'object' && item._listTitle) {
        return String(item._listTitle)
      }
      return this.selectItemTitle(item)
    },
    // Bridges each item's TOP-LEVEL `disabled` -- the V2 shape every
    // services/selectLists/*Statii.ts still authors -- onto the `item.props` shape V3
    // reads, merging rather than replacing so divider `inset` and getSelectList's own
    // `props` survive. Bound as a BARE METHOD REFERENCE on purpose: Vuetify's items
    // computed depends on the identity of `item-props` (transformItems does
    // `pick(props, transformItem.neededProps)`), so an inline arrow would re-transform
    // every item on every render. Same reasoning as selectValueComparators above.
    selectItemProps,
    enforceNumberFormat,
    enforceCurrencyFormat,
    enforcePercentageFormat,
    getClearFieldValue,
    // Re-exposed as methods because a plain (non-`<script setup>`) SFC template
    // resolves names against the instance, not the module scope.
    isoDateStringToDate,
    dateToIsoDateString,
    //
    doDisableWidget(
      ifield: TEntityMaskItem,
      inputFields: TEntityMaskItem[],
      index: number
    ) {
      return doDisableWidget(ifield, inputFields, index)
    },
    doMakeWidgetReadonly(ifield: TEntityMaskItem) {
      return doMakeWidgetReadonly(ifield, this.isViewMode)
    },
    // Every tab change goes through here, so the opening height is captured
    // before the first one. Measured at the moment of leaving rather than at
    // open because the select lists load asynchronously and fields render and
    // unrender after open -- an early measurement would lock in a short form.
    selectMaskTab(key: unknown) {
      if (typeof key !== 'string' || key === this.activeTabKey) {
        return
      }
      if (this.maskFieldsMinHeight === null) {
        const row = (this.$refs.maskFieldsRow as any)?.$el as
          | HTMLElement
          | undefined
        if (row?.offsetHeight) {
          this.maskFieldsMinHeight = row.offsetHeight
        }
      }
      this.activeTabKey = key
    },
    // The opt-in described at the v-combobox: only a list field or a spec that
    // sets `itemValue` gets these, so Address Line 1 keeps Vuetify's combobox
    // defaults exactly. Returned as an object so the props are truly ABSENT
    // otherwise, not bound as undefined.
    hasComboboxItemBindings(ifield: IEntityMaskData): boolean {
      return !!ifield.itemValue || isDelimitedListField(ifield)
    },
    onComboboxMenuUpdate(ifield: IEntityMaskData, open: boolean) {
      if (open && !ifield.hasUserInteraction) {
        return
      }
      ifield.isMenuOpen = open
    },
    // Readonly (including view mode) or disabled: the user cannot change it.
    isComboboxLocked(ifield: IEntityMaskData, index: number): boolean {
      return (
        this.doMakeWidgetReadonly(ifield) ||
        this.doDisableWidget(ifield, this.inputFields, index)
      )
    },
    comboboxItemBindings(
      ifield: IEntityMaskData,
      index: number
    ): Record<string, unknown> {
      if (!this.hasComboboxItemBindings(ifield)) {
        return {}
      }
      return {
        // No disclosure triangle on a list that cannot be changed.
        ...(this.isComboboxLocked(ifield, index) ? { menuIcon: false } : {}),
        // The list opens only after the user has touched THIS field. Vuetify
        // also opens a focused combobox's list when its items finish loading,
        // so the field focusOnFirstField picks (To) could drop its list open by
        // itself, depending on how the contacts load raced the focus.
        menu: ifield.isMenuOpen === true,
        'onUpdate:menu': (open: boolean) =>
          this.onComboboxMenuUpdate(ifield, open),
        onKeydownCapture: () => {
          ifield.hasUserInteraction = true
        },
        onMousedownCapture: (e: MouseEvent) => {
          ifield.hasUserInteraction = true
          this.onComboboxLockedMousedownCapture(e, ifield, index)
        },
        itemTitle: this.selectItemTitle,
        // Names the property a chip shows and the save stores. returnObject
        // stays at VCombobox's default (true), so a pick keeps the whole row.
        // Absent for a list of plain strings, whose entries ARE the value.
        ...(ifield.itemValue ? { itemValue: ifield.itemValue } : {}),
        // Typing matches the label OR the stored value (e.g. the address).
        filterKeys: ['title', 'value'],
        closableChips: true,
        // Typing or pasting "a@x.com, b@y.com" makes two chips.
        delimiters: [',', ';'],
      }
    },
    // Vuetify's own chip mousedown does preventDefault + stopPropagation, so a
    // click on a chip never reaches the field and the menu cannot open from
    // it. Once chips stack up they cover most of the field, leaving only the
    // gaps clickable. Keep the preventDefault (focus stays in the input) but let
    // the event bubble to the field -- which opens the menu -- unless it is on
    // the chip's close X, where opening the menu while removing a chip would be
    // wrong.
    //
    // A locked field never gets here: onComboboxLockedMousedownCapture stops
    // the mousedown at the field's root first.
    onComboboxChipMousedown(e: MouseEvent) {
      e.preventDefault()
      if ((e.target as HTMLElement | null)?.closest('.v-chip__close')) {
        e.stopPropagation()
      }
    },
    // Vuetify 3 FOCUSES a readonly field on click (see
    // reference_v3_focuses_readonly_inputs): its menu stays shut, but the label
    // turns primary and an empty input line opens under the chips, so a
    // read-only recipient list looks editable. For a readonly / view-mode /
    // disabled field, stop the mousedown in the CAPTURE phase at the field's
    // root, before VField's handler (which focuses the input) or a chip's
    // handler runs; preventDefault also blocks the input's native focus.
    // Keyboard focus (Tab) is untouched.
    onComboboxLockedMousedownCapture(
      e: MouseEvent,
      ifield: IEntityMaskData,
      index: number
    ) {
      if (!this.isComboboxLocked(ifield, index)) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
    },
    isInActiveTab(index: number): boolean {
      return isIndexInActiveTab(this.maskTabLayout, index, this.activeTabKey)
    },
    isDirtyIField(ifield: TEntityMaskItem) {
      return isDirtyIField(ifield, this.isEditing)
    },
    clearModelValue(ifield: TEntityMaskItem) {
      const fn = `${mn} clearModelValue |`
      if (!isEntityMaskData(ifield)) {
        return
      }
      console.debug(`${fn} RECEIVED EMIT: clearModelValue`)
      ifield.start = undefined
      ifield.value = undefined
    },
    goEditMode() {
      // Keeps the lock icon inert too (it also calls goEditMode) when editing
      // is disabled for the current user.
      if (this.disableEditButton) {
        return
      }
      this.isViewMode = false
      this.setStartingDialogTitlePrefix()
    },
    // Whether a field already holds something, for focusOnFirstField. An
    // address list's empty value is [], which `!!value` calls filled -- so an
    // empty To would be skipped. Other fields keep the plain truthiness test.
    hasFocusBlockingValue(ifield: IEntityMaskData): boolean {
      // A field whose preset text is written ABOVE (a message's signature)
      // still wants the cursor.
      if (ifield.autoFocusAtStart) {
        return false
      }
      if (ifield.delimitedStringSeparator !== undefined) {
        return Array.isArray(ifield.value) && ifield.value.length > 0
      }
      return !!ifield.value
    },
    focusOnFirstField() {
      const fn = `${mn} focusOnFirstField |`
      if (
        !this.hasInputFields ||
        this.suppressFocusOnFirstField ||
        this.isViewMode
      ) {
        return
      }
      // The first field to focus: the first rendered, focusable field (not
      // readonly / disabled / display-only) -- and on CREATE, skip any that already
      // carry a preset value (e.g. a defaulted <v-select> that precedes the first
      // empty text field, as on the Service Groups create dialog).
      //
      // Focus it via the field component's own ref/.focus(), NOT a raw-DOM walk of
      // the form's controls (the previous approach). Vuetify 3 renders a
      // non-searchable <v-select>'s <input> as `readonly`, so a control-index walk
      // skips it -- while the value-based skip count ALSO skips it -- double-skipping
      // past the real first field and leaving nothing focused. Per-field .focus()
      // sidesteps that, and focuses a <v-file-input> (whose native <input> is
      // unfocusable) for free. renderUnrenderInputFields()/enableDisableInputFields()
      // have already run, so these flags are current.
      const targetIdx = this.inputFields.findIndex(
        (f, i) =>
          this.isInActiveTab(i) &&
          isEntityMaskData(f) &&
          !f.unrendered &&
          !f.readonly &&
          !f.disabled &&
          !('isForDisplayOnly' in f) &&
          !f.skipAutoFocus &&
          (this.isEditing || !this.hasFocusBlockingValue(f))
      )
      if (targetIdx < 0) {
        console.debug(`${fn} no focusable first field found`)
        return
      }
      this.$nextTick(() => {
        setTimeout(() => {
          const r = this.$refs['ifield-' + targetIdx]
          const cmp = Array.isArray(r) ? r[0] : r
          ;(cmp as any)?.focus?.()
          const target = this.inputFields[targetIdx]
          if (target && isEntityMaskData(target) && target.autoFocusAtStart) {
            // The caret at the very start, above the preset text, which is
            // scrolled back into view from wherever focusing left it.
            const el = (cmp as any)?.$el?.querySelector?.('textarea, input') as
              | HTMLTextAreaElement
              | HTMLInputElement
              | null
            el?.setSelectionRange(0, 0)
            if (el) {
              el.scrollTop = 0
            }
          }
        }, 0)
      })
    },
    focusOnFirstErrorField() {
      const fn = `${mn} focusOnFirstErrorField |`
      // An error on a tab the user is not looking at would otherwise leave Save
      // doing nothing visible. Switch first, so the error is on screen by the
      // time the focus below runs.
      const tabForErrors = resolveTabForErrors({
        visibleTabs: this.visibleMaskTabs,
        activeTabKey: this.activeTabKey,
        tabKeysWithErrors: this.tabKeysWithErrors,
      })
      if (tabForErrors !== null) {
        this.selectMaskTab(tabForErrors)
      }
      this.$nextTick(() => {
        // Asserted rather than left to inference: querySelector is typed to
        // Element, which has no focus(). The @ts-expect-error that used to sit
        // on the call below had gone UNUSED — not because the call became safe
        // but because `this.$el` degrades to `any`, which stops the expression
        // being checked at all. Naming the type restores the check.
        const el = this.$el.querySelector(
          '.v-input .text-error:first-of-type'
        ) as HTMLElement | null
        console.debug(`${fn} Trying to focus on first error field:`, el)
        el?.focus()
      })
    },
    clearValidationsArray() {
      const fn = `${mn} clearValidationsArray |`
      this.fieldValidations = [] as unknown as Function[][]
      console.debug(`${fn} after clearing array...`)
      console.debug(this.fieldValidations)
    },
    clearShowIsValidatedCheckmark() {
      this.showIsValidatedCheckmark = false
    },
    clearSaveAndAddAnotherClickedFlag() {
      this.saveAndAddAnotherClicked = false
    },
    fieldClass(ifield: TEntityMaskItem) {
      return getFieldClass(ifield, this.isViewMode)
    },
    // A spec's noDataText speaks of the list being EMPTY ("This opportunity's
    // company has no locations set up yet."). Shown for a search that merely
    // matched nothing, it would be wrong.
    autocompleteNoDataText(
      ifield: IEntityMaskData,
      index: number
    ): string | undefined {
      return this.autocompleteSearchTexts[index] && ifield.items?.length
        ? 'No matches'
        : ifield.noDataText
    },
    autocompleteSearchHasMatches(
      ifield: IEntityMaskData,
      index: number
    ): boolean {
      return autocompleteSearchHasMatches(
        ifield,
        this.autocompleteSearchTexts[index]
      )
    },
    doAddNewOptionToSelect(ifield: IEntityMaskData) {
      return doAddNewOptionToSelect(ifield, {
        isSubentityDialog: this.isSubentityDialog,
        isEditing: this.isEditing,
      })
    },
    isFieldMultiple(ifield: IEntityMaskData): boolean {
      return isFieldMultiple(ifield, this.inputFields)
    },
    doShowSelectAll(ifield: IEntityMaskData) {
      return doShowSelectAll(ifield, this.inputFields, this.isViewMode)
    },
    isSelectAllEnabled(ifield: IEntityMaskData) {
      return isSelectAllEnabled(ifield, this.inputFields)
    },
    // Select every non-disabled option (items disabled via their `disabled`
    // key — e.g. supplierProductItem's enforceHomogeneousProductSelection — are
    // excluded), then run the normal post-change pipeline (a programmatic
    // v-model set does not emit update:model-value).
    selectAllItems(index: number) {
      const fn = `${mn} selectAllItems |`
      const ifield = this.inputFields[index]
      assertIsAnEntityMaskData(fn, ifield)
      const items = (this.getProcessedItems(ifield) || []) as any[]
      const selectable = items.filter(
        (i) => i && typeof i === 'object' && !i.disabled
      )
      ifield.value = ifield.doReturnObject
        ? selectable
        : selectable.map((i) => i[ifield.itemValue || 'id'])
      this.selectChanged(index)
    },
    // Dismiss a select's dropdown menu (blur-in-nextTick; v-for refs are arrays).
    closeSelectMenu(index: number) {
      this.$nextTick(() => {
        const refs = this.$refs['ifield-' + index]
        if (refs && (refs as any[])[0]) {
          ;((refs as any[])[0] as any).blur()
        }
      })
    },
    shouldCloseMenuOnAllSelected(ifield: IEntityMaskData) {
      return shouldCloseMenuOnAllSelected(ifield, {
        items: this.getProcessedItems(ifield),
        inputFields: this.inputFields,
        isViewMode: this.isViewMode,
      })
    },
    addNewOptionToSelect(index: number) {
      const fn = `${mn} addNewOptionToSelect |`
      this.index = index // sync this.index based on ifield owning select in question
      const inputField = this.inputFields[index]
      assertIsAnEntityMaskData(fn, inputField)
      console.debug(
        `${fn} DROP-DOWN OPTION CLICKED:` +
          ` NEW ${inputField.subentity}). Index: ${index}`
      )
      assertIsNotUndefined(inputField.subentity, fn, 'inputField.subentity') // should never be if we're calling this function
      // Carry the name typed in the picker into the NEW-person dialog so it opens
      // pre-filled and the "did you mean…?" match fires (#113). Person subentity
      // only; other subentity creates keep their existing pass-through values.
      const passedValues =
        this.getValuesToPassToSubentityDialog(inputField) ?? []
      const prefillValues =
        inputField.subentity === 'person'
          ? personNamePrefillValues(this.autocompleteSearchTexts[index])
          : []
      setShowSubentityDialog({
        callingMn: mn,
        entityName: inputField.subentity,
        ...(inputField.subentityInputFieldsEntityNameOverride && {
          subentityInputFieldsEntityNameOverride:
            inputField.subentityInputFieldsEntityNameOverride,
        }),
        subentityNameTitleOverride: inputField.subentityNameTitleOverride,
        subentityAfterRequestProcessor:
          inputField.subentityAfterRequestProcessor,
        subentityParentObjectForCreate:
          inputField.subentityParentObjectForCreate,
        valuesForSubentityFieldsWhoseValuesToSetFromEntityFields: [
          ...passedValues,
          ...prefillValues,
        ],
      })
    },
    getValuesToPassToSubentityDialog(
      inputField: IEntityMaskData
    ): IValueToPassToSubentityDialog[] | undefined {
      const fn = `${mn} getValuesToPassToSubentityDialog |`
      // const valuesToPassToSubentityDialogWhoseValuesWereSetFromEntityFields =
      //   !!inputField.namesOfSubentityFieldsWhoseValuesToSetFromEntityFields
      //     ? inputField.namesOfSubentityFieldsWhoseValuesToSetFromEntityFields.map(
      //         (sourceIFieldName) => {
      //           console.debug(`${fn} sourceIFieldName:`, sourceIFieldName)
      //           const sourceIField = this.inputFields.find(
      //             (ifield) => ifield.name === sourceIFieldName
      //           )
      //           if (sourceIField) {
      //             console.debug(`${fn} sourceIField:`, sourceIField)
      //             return {
      //               fieldName: sourceIField.name,
      //               value: sourceIField.value,
      //             }
      //           }
      //         }
      //       )
      //     : undefined

      const that = this
      const valuesToPassToSubentityDialogWhoseValuesWereSetFromEntityFields =
        !inputField.namesOfSubentityFieldsWhoseValuesToSetFromEntityFields
          ? []
          : inputField.namesOfSubentityFieldsWhoseValuesToSetFromEntityFields.reduce(
              function (result, sourceIFieldName) {
                console.debug(`${fn} sourceIFieldName:`, sourceIFieldName)
                const sourceIField = that.inputFields.find(
                  (ifield) => ifield.name === sourceIFieldName
                )
                if (sourceIField) {
                  assertIsAnEntityMaskData(fn, sourceIField) // always should be
                  console.debug(`${fn} sourceIField:`, sourceIField)
                  result.push({
                    fieldName: sourceIField.name,
                    value: sourceIField.value,
                  })
                }
                return result
              },
              [] as IValueToPassToSubentityDialog[]
            )

      console.debug(
        `${fn} valuesToPassToSubentityDialogWhoseValuesWereSetFromEntityFields:`,
        valuesToPassToSubentityDialogWhoseValuesWereSetFromEntityFields
      )
      // if (!valuesToPassToSubentityDialogWhoseValuesWereSetFromEntityFields) {
      //   return undefined
      // }
      // Concatenate values obtained from specified inputFields with
      // explicit values in this inputField's valuesToPassToSubentityDialog
      // property
      const valuesToPassToSubentityDialog = [
        ...valuesToPassToSubentityDialogWhoseValuesWereSetFromEntityFields,
        ...(inputField.valuesToPassToSubentityDialog
          ? inputField.valuesToPassToSubentityDialog
          : []),
      ]

      console.debug(`${fn} valuesToPass:`, valuesToPassToSubentityDialog)
      return valuesToPassToSubentityDialog
    },

    // getProcessedItems(ifield) {
    //   return !this.isSubentityDialog && // At the moment, not allowing nested subentity 'NEW'
    //     !!ifield.subentity &&
    //     (!this.isEditing ||
    //       (this.isEditing && ifield.allowCreateNewSubentityWhenEditing))
    //     ? [{ id: 'NEW', label: 'NEW' }].concat(ifield.items)
    //     : ifield.items
    // },
    getProcessedItems(ifield: IEntityMaskData) {
      return ifield.items
    },

    // Person picker (#113): the items to show, so near-misses the substring
    // filter would hide still appear. All persons are already loaded in
    // `ifield.items`; when there is a search we keep those whose label contains
    // it OR whose id is in the server's fuzzy match set for this field. Reads
    // `autocompleteSearchTexts`/`personFuzzyMatchIds` so it recomputes when the
    // debounced server call lands. The field uses :no-filter, so this list is
    // shown as-is. Gated to subentity==='person'.
    getPersonPickerItems(ifield: IEntityMaskData, index: number): any[] {
      const base = (this.getProcessedItems(ifield) || []) as any[]
      const query = (this.autocompleteSearchTexts[index] || '')
        .trim()
        .toLowerCase()
      if (!query) {
        return base
      }
      const fuzzy = this.personFuzzyMatchIds[index]
      return base.filter((item) => {
        const label = String(this.selectItemTitle(item) ?? '').toLowerCase()
        return label.includes(query) || (!!fuzzy && fuzzy.has(String(item?.id)))
      })
    },

    // Whether the person picker currently shows any match (substring or fuzzy).
    // Used to demote the "NEW" option behind server candidates. No query -> we
    // treat it as "no matches" so NEW behaves as it did before typing.
    personPickerHasVisibleMatches(
      ifield: IEntityMaskData,
      index: number
    ): boolean {
      const query = (this.autocompleteSearchTexts[index] || '').trim()
      if (!query) {
        return false
      }
      return this.getPersonPickerItems(ifield, index).length > 0
    },

    // @update:search for autocompletes. Records the search text and, for a
    // person picker, debounces a fuzzy server lookup that fills
    // `personFuzzyMatchIds[index]`.
    onAutocompleteSearch(
      index: number,
      ifield: IEntityMaskData,
      val: string
    ): void {
      this.autocompleteSearchTexts[index] = val
      if (ifield.subentity !== 'person') {
        return
      }
      let debounced = this.personFuzzyDebouncers[index]
      if (!debounced) {
        debounced = debounce(async (text: string) => {
          const ids = await matchPersonIdsBySearch(text)
          // Replace the whole record so the getter recomputes reactively.
          this.personFuzzyMatchIds = {
            ...this.personFuzzyMatchIds,
            [index]: ids,
          }
        }, 250)
        this.personFuzzyDebouncers[index] = debounced
      }
      debounced(val)
    },
    setFieldValidations() {
      const fn = `${mn} setFieldValidations |`
      this.clearValidationsArray()
      const arrayLength = this.inputFields.length
      this.fieldValidations = Array(arrayLength)
      for (let i = 0; i < arrayLength; i++) {
        const thisIField = this.inputFields[i]
        if (isEntityMaskData(thisIField)) {
          const validations = this.getFieldValidations(thisIField)
          if (validations) {
            this.fieldValidations[i] = validations
          }
        }
      }
      console.debug(`${fn}...`)
      console.debug(this.fieldValidations)
    },
    getFieldValidations(ifield: IEntityMaskData): Function[] | undefined {
      return buildFieldValidations(ifield, {
        inputFields: this.inputFields as IEntityMaskData[],
        entityParentObjectForCreate: this.entityParentObjectForCreate,
        entityData: this.entityData as { parentMSServiceId?: unknown },
      })
    },
    doValidation({
      callback,
      wasClickedSaveAndAddAnother,
    }: {
      callback: Function
      wasClickedSaveAndAddAnother: boolean
    }) {
      const fn = `${mn} doValidation |`
      const that = this
      setTimeout(async function () {
        // Vuetify 3: VForm.validate() is async and resolves to { valid, errors }
        // (V2 returned a plain boolean) — same change already handled in
        // components/loginLayout/LoginCard.vue. The previous `=== true` compared
        // a *Promise* and so was ALWAYS false, which made doAfterValidation bail
        // on every save: no request, no console error, no field marked invalid,
        // dialog left open. Silent, and it blocked every create/edit in the app.
        // `?? {}` keeps the old fail-closed behavior if the ref is missing.
        const { valid: isValid = false } =
          (await that.$refs.entityMask?.validate()) ?? {}
        console.debug(`${fn} Returning isValid = ${isValid}`)
        callback({ isValid, wasClickedSaveAndAddAnother })
      })
    },
    doAfterValidation({
      isValid,
      wasClickedSaveAndAddAnother,
    }: {
      isValid: boolean
      wasClickedSaveAndAddAnother: boolean
    }) {
      const fn = `${mn} doAfterValidation |`
      if (!isValid) {
        console.debug(`${fn} Form data is NOT valid. Not saving.`)
        this.focusOnFirstErrorField()
        return
      }
      const newOrUpdatedEntityData = this.getDirtyFieldsObject()
      this.clearValidationsArray()
      this.showIsValidatedCheckmark = true
      this.$emit('save', {
        newOrUpdatedEntityData,
        wasClickedSaveAndAddAnother,
      })
    },
    getDirtyFieldsObject() {
      const fn = `${mn} getDirtyFieldsObject |`
      const dirtyFieldsObject = getDirtyFieldsObject({
        inputFields: this.inputFields,
        entityData: this.entityData,
        isEditing: this.isEditing,
      })
      console.debug(`${fn} dirtyFieldsObject...`)
      console.debug(dirtyFieldsObject)
      return dirtyFieldsObject
    },
    compensateForClearableClearSettingWrongFalseyValue(
      ifield: IEntityMaskData
    ) {
      return compensateForClearableClearSettingWrongFalseyValue(ifield)
    },
    textFieldChanged(index: number) {
      const fn = `${mn} textFieldChanged |`
      console.debug(`${fn} index = ${index}`)
      this.updateUnrenderedDisabled(index)
      const ifield = this.inputFields[index]
      assertIsAnEntityMaskData(fn, ifield)
      console.debug(
        `${fn} before running runOnChanged, textField ${ifield.name}.value =`,
        ifield.value
      )
      if (ifield.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
        console.debug(
          `${fn} after running runOnChanged, textField ${ifield.name}.value =`,
          ifield.value
        )
      }
    },
    selectChanged(index: number) {
      const ifield = this.inputFields[index]
      const fn = `${mn} selectChanged (${ifield.name}) |`
      assertIsAnEntityMaskData(fn, ifield)
      this.handleClearOtherAndClearOtherItemsDirectives(
        fn,
        ifield,
        this.inputFieldsOnlyIEntityMaskData
      )
      console.debug(`${fn} index = ${index}`)
      console.debug(`${fn} select 'value' (id) =`, ifield.value)

      ifield.value =
        this.compensateForClearableClearSettingWrongFalseyValue(ifield)

      this.renderUnrenderInputFields()
      this.enableDisableInputFields()
      // this.modifyDialogTitlePrefix(ifield) // currently a no-op

      // Note: It does not work to move the above statements below
      // the if block (ifield.runOnChanged) below and make the run
      // on changed and this method async. If you need to re-run
      // renderUnrender and enableDisable -after- runOnChanged,
      // make a 'that.updateUnrenderedDisabled()' call -inside- the
      // runOnChanged method definition in the appropriate input spec
      // stanza.

      if (ifield.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
      }

      // Dismiss the dropdown once all eligible options are selected (via Select
      // All or manual picks). Runs after runOnChanged so the item disabled flags
      // (set synchronously by the field's enforcement) are current.
      if (this.shouldCloseMenuOnAllSelected(ifield)) {
        this.closeSelectMenu(index)
      }
    },
    autocompleteChanged(index: number) {
      const fn = `${mn} autocompleteChanged |`
      // const CREATE_NEW = 'NEW'
      console.debug(`${fn} index = ${index}`)
      const ifield = this.inputFields[index]
      assertIsAnEntityMaskData(fn, ifield)
      this.handleClearOtherAndClearOtherItemsDirectives(
        fn,
        ifield,
        this.inputFieldsOnlyIEntityMaskData
      )
      console.debug(`${fn} combobox 'value' =`, ifield.value)
      this.renderUnrenderInputFields()
      this.enableDisableInputFields()

      // (See note in selectChanged method)

      if (ifield.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
      }
    },
    // Bound to @update:model-value (see the note above the select family).
    // NOTE, if a combobox field ever grows a runOnChanged or a clearOther*
    // directive: unlike a select, Vuetify 3's VCombobox emits
    // update:modelValue on every KEYSTROKE, not once on commit -- its model IS
    // the free text. That is harmless today because no `type: 'combobox'`
    // field declares either, so this reduces to the idempotent unrender/disable
    // passes. A handler that clears sibling fields would fire per character
    // instead.
    comboboxChanged(index: number) {
      const fn = `${mn} comboboxChanged |`
      // const CREATE_NEW = 'NEW'
      console.debug(`${fn} index = ${index}`)
      const ifield = this.inputFields[index]
      assertIsAnEntityMaskData(fn, ifield)
      this.handleClearOtherAndClearOtherItemsDirectives(
        fn,
        ifield,
        this.inputFieldsOnlyIEntityMaskData
      )
      console.debug(`${fn} combobox 'value' =`, ifield.value)
      this.renderUnrenderInputFields()
      this.enableDisableInputFields()

      // (See note in selectChanged method)

      if (ifield.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
      }
    },
    checkboxChanged(index: number) {
      const fn = `${mn} checkboxChanged |`
      console.debug(`${fn} index: ${index}`)
      if (this.inputFields.length < index + 2) {
        // We're looking to see if this checkbox might be associated
        // with the next inpurField, typically a date or a textfield.
        // It can't be if there isn't at least one more inputField.
        return
      }
      const thisIField = this.inputFields[index]
      assertIsAnEntityMaskData(fn, thisIField) // should always be (it's a checkbox)
      const nextIField = this.inputFields[index + 1]

      if (
        isEntityMaskData(nextIField) &&
        thisIField.value === false &&
        nextIField.tiedToPriorFieldCheckbox &&
        nextIField.value
      ) {
        console.debug(`${fn} clearing field`)
        nextIField.value = null
      }

      this.renderUnrenderInputFields()
      this.enableDisableInputFields()

      // (See note in selectChanged method)

      if (thisIField.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        thisIField.runOnChanged(
          thisIField,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
      }
    },
    textAreaChanged(index: number) {
      const fn = `${mn} textAreaChanged |`
      console.debug(`${fn} index = ${index}`)
      const ifield = this.inputFields[index]
      assertIsAnEntityMaskData(fn, ifield)
      console.debug(
        `${fn} before running runOnChanged, textArea ${ifield.name}.value =`,
        ifield.value
      )
      if (ifield.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFieldsOnlyIEntityMaskData,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
        console.debug(
          `${fn} after running runOnChanged, textArea ${ifield.name}.value =`,
          ifield.value
        )
      }
    },
    timePickerChanged(ifield: IEntityMaskData, value: unknown) {
      const fn = `${mn} timePickerChanged |`
      ifield.value = value as typeof ifield.value

      this.renderUnrenderInputFields()
      this.enableDisableInputFields()

      // (See note in selectChanged method)

      console.debug(
        `${fn} before running runOnChanged, timePicker ${ifield.name}.value =`,
        ifield.value
      )
      if (ifield.runOnChanged) {
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
        console.debug(
          `${fn} after running runOnChanged, timePicker ${ifield.name}.value =`,
          ifield.value
        )
      }
    },
    // AM / PM is the last step of a time typed into the header, so it closes
    // the menu -- but only once there is a full time, so choosing the period
    // first and then picking on the clock still works. A DOM click, because
    // VTimePicker emits update:period only when the period CHANGES, and
    // clicking the one already set (AM, on an empty picker) emits nothing.
    timePickerClicked(ifield: IEntityMaskData, event: MouseEvent) {
      const target = event.target as Element | null
      if (
        target?.closest('.v-time-picker-controls__ampm__btn') &&
        ifield.value
      ) {
        ifield.showPicker = false
      }
    },
    // Return: on AM / PM, press it (the dialog's overlay stack swallows the
    // click a focused button would get from Return, so the period was never
    // applied and the menu stayed open); anywhere else in the picker, close it
    // once there is a full time.
    timePickerEnter(ifield: IEntityMaskData, event: KeyboardEvent) {
      const periodButton = (event.target as Element | null)?.closest(
        '.v-time-picker-controls__ampm__btn'
      )
      event.preventDefault()
      if (periodButton instanceof HTMLElement) {
        periodButton.click()
      } else if (ifield.value) {
        ifield.showPicker = false
      }
    },
    // Boundary between VDatePicker's Date and the field's 'YYYY-MM-DD' string
    // (see datePickerValue.ts). Passing didClickDate reproduces Vuetify 2's
    // @click:date, which is what closed the menu once a day was picked -- V3
    // emits only update:modelValue, so the one handler does both jobs.
    datePickerPicked(ifield: IEntityMaskData, picked: unknown) {
      ifield.value = dateToIsoDateString(picked)
      this.datePickerChanged({ ifield, didClickDate: true })
    },
    datePickerChanged({
      ifield,
      didClickDate = false,
    }: {
      ifield: IEntityMaskData
      didClickDate?: boolean
    }) {
      const fn = `${mn} datePickerChanged |`

      this.renderUnrenderInputFields()
      this.enableDisableInputFields()

      // (See note in selectChanged method)

      if (ifield.value === null) {
        // If user clicks the 'X' clearable button in the date-field widget,
        // Vuetify sets the value to null instead of an empty string (which is
        // the default start value). The line below fixes that so
        // ifield.start === ifield.value and isDirty evaluates to false for
        // a field that started out blank.

        ifield.value = ''
      }

      console.debug(
        `${fn} before running runOnChanged, datePicker ${ifield.name}.value =`,
        ifield.value
      )
      if (ifield.runOnChanged) {
        console.debug(`${fn} has runOnChanged value`)
        const that = this // Sometimes need access to things like the store
        ifield.runOnChanged(
          ifield,
          this.inputFields,
          this.isSubentityDialog,
          that,
          this.entityParentObjectForCreate
        )
        console.debug(
          `${fn} after running runOnChanged, datePicker ${ifield.name}.value =`,
          ifield.value
        )
      }
      if (didClickDate) {
        console.debug(`${fn} did click date. Saving and closing.`)
        ifield.showPicker = false
      }
    },
    // Bound to @update:model-value, NOT @change. Vuetify 2's v-file-input
    // emitted the File/File[] on `change`; Vuetify 3's `change` is the native
    // DOM event, so the old binding stored an Event as the field's value and the
    // upload POSTed `file: Event` — no file ever reached the server.
    filesChanged(files: File | File[], index: number) {
      const fn = `${mn} filesChanged |`
      console.debug(`${fn} Files:`, files)
      console.debug(`${fn} Index:`, index)
      this.inputFields[index].value = files
      console.debug(`${fn} this.inputFields[${index}]...`)
      console.debug(this.inputFields[index])
    },
    // The file field's outer prepend icon is decorative. Vuetify hardcodes its
    // click to call input.click() (opening the picker) and its mousedown steals
    // focus from the field. Intercept both in the capture phase and fully
    // suppress them — but only for the prepend-outer icon, not the field itself.
    suppressFileInputPrependIcon(e: Event) {
      const target = e.target as HTMLElement | null
      if (target?.closest('.v-input__prepend-outer')) {
        e.preventDefault()
        e.stopPropagation()
      }
    },

    handleClearOtherAndClearOtherItemsDirectives(
      fn: string,
      ifield: IEntityMaskData,
      inputFields: IEntityMaskData[]
    ) {
      // Note: clearOtherOnChanged fields, clearOtherOnChangedIfNotIsEditing fields,
      //  and clearOtherItemsOnChanged fields are not necessarily the same, so they
      // need to be separate iterators below.
      //
      // TODO: This should probably use the 'getClearFieldValue' function to get
      // the value to assign when cleared. JR.
      //
      if (ifield.clearOtherOnChanged) {
        console.debug(
          `${fn} 1 Going to clear some values...`,
          ifield.clearOtherOnChanged
        )
        ifield.clearOtherOnChanged.forEach((fldToClrName: string) => {
          const ifieldToClear = inputFields.find(
            (ifd: IEntityMaskData) => ifd.name === fldToClrName
          )
          assertIsNotUndefined(ifieldToClear, fn)
          console.debug(
            `${fn} 2 Going to clear '${ifieldToClear?.name}`,
            ifieldToClear
          )
          if (ifieldToClear?.ignoreClearOnChangedRequests) {
            console.debug(
              `${fn} ignoreClearOnChangedRequests is true for ${ifieldToClear.name}. Not clearing.`,
              ifieldToClear
            )
          } else {
            // ⚠ NOT `ifieldToClear.start` on create. A field using the
            // force-dirty idiom (`start: undefined` + `default: null`) must
            // clear to NULL, or it stops being dirty and its property vanishes
            // from the request -- which is how a Case POST lost `contact`.
            // getClearFieldValueFromStart owns that decision, synchronously,
            // because this runs inside selectChanged and cannot await.
            // ⚠ The EDIT branch is deliberately left as a bare null: routing it
            // through the same function would change a cleared text field from
            // null to '' on update.
            ifieldToClear.value = !this.isEditing
              ? getClearFieldValueFromStart(ifieldToClear)
              : null
          }
        })
      }
      if (ifield.clearOtherOnChangedIfNotIsEditing && !this.isEditing) {
        console.debug(
          `${fn} Not editing, s going to clear some values...`,
          ifield.clearOtherOnChangedIfNotIsEditing
        )
        ifield.clearOtherOnChangedIfNotIsEditing.forEach((fldToClrName) => {
          const ifieldToClear = inputFields.find(
            (ifd) => ifd.name === fldToClrName
          )
          assertIsNotUndefined(ifieldToClear, fn)
          console.debug(
            `${fn} 3 Going to clear '${ifieldToClear.name}`,
            ifieldToClear
          )
          // Create-only directive, same idiom hazard as above.
          ifieldToClear.value = getClearFieldValueFromStart(ifieldToClear)
        })
      }
      if (ifield.clearOtherItemsOnChanged) {
        console.debug(
          `${fn} 4 Going to clear some items...`,
          ifield.clearOtherItemsOnChanged
        )
        ifield.clearOtherItemsOnChanged.forEach((fldToClrName) => {
          const ifieldToClear = inputFields.find(
            (ifd) => ifd.name === fldToClrName
          )
          assertIsNotUndefined(ifieldToClear, fn)
          console.debug(`${fn} Going to clear '${ifieldToClear.name} items`)
          ifieldToClear.items = []
        })
      }
      if (ifield.clearOtherOnCleared && !ifield.value) {
        console.debug(
          `${fn} 5 Going to clear some values...`,
          ifield.clearOtherOnCleared
        )
        ifield.clearOtherOnCleared.forEach((fldToClrName) => {
          const ifieldToClear = inputFields.find(
            (ifd) => ifd.name === fldToClrName
          )
          assertIsNotUndefined(ifieldToClear, fn)
          console.debug(
            `${fn} 6 Going to clear '${ifieldToClear.name}`,
            ifieldToClear
          )
          if (ifieldToClear.ignoreClearOnChangedRequests) {
            console.debug(
              `${fn} ignoreClearOnChangedRequests is true for this field. Not clearing '${ifieldToClear.name}`,
              ifieldToClear
            )
          } else {
            ifieldToClear.value = !this.isEditing
              ? getClearFieldValueFromStart(ifieldToClear)
              : null
            const idx = this.inputFields.indexOf(ifieldToClear)
            if (idx !== -1) {
              const ref = this.$refs['ifield-' + idx]
              if (ref && (ref as any[])[0]) {
                ;((ref as any[])[0] as any).resetValidation()
              }
            }
          }
        })
      }
    },

    cancelSearchDebouncers() {
      Object.values(this.searchDebouncers).forEach((d) => d.cancel())
      this.searchDebouncers = markRaw(
        {} as Record<number, DebouncedFunc<(searchText: string) => void>>
      )
    },

    // Bound to @update:search on the combobox. Where setSelectItems loads a
    // whole list once, runOnSearch is handed each search term so a spec can go
    // and fetch a remotely-ranked list — see addressFieldsCommonNoDividers.ts.
    //
    // Opt-in: a field with no runOnSearch costs one lookup and a return, which
    // matters because VCombobox emits update:search on every keystroke.
    onComboboxSearch(index: number, searchText: string) {
      const ifield = this.inputFields[index]
      if (!ifield || !isEntityMaskData(ifield) || !ifield.runOnSearch) {
        return
      }
      let debounced = this.searchDebouncers[index]
      if (!debounced) {
        const fieldName = ifield.name
        debounced = debounce((text: string) => {
          // Re-read rather than closing over `ifield`: by the time the timer
          // fires the dialog may have closed or re-opened on another entity.
          const current = this.inputFields?.[index]
          if (
            !current ||
            !isEntityMaskData(current) ||
            current.name !== fieldName ||
            !current.runOnSearch
          ) {
            return
          }
          Promise.resolve(
            current.runOnSearch({
              searchText: text,
              ifield: current,
              inputFields: this.inputFields,
              entityParentObjectForCreate: this.entityParentObjectForCreate,
              that: this,
            })
          ).catch((e) => {
            // Nothing upstream can catch this: we are inside a timer.
            console.error(`${mn} runOnSearch (${fieldName}) failed.`, e)
          })
        }, ifield.runOnSearchDebounceMs ?? DEFAULT_RUN_ON_SEARCH_DEBOUNCE_MS)
        this.searchDebouncers[index] = debounced
      }
      debounced(searchText)
    },

    // Re-open a field's menu after its items have been replaced.
    //
    // Best-effort by design. VCombobox's select() closes the menu inside its own
    // nextTick, and a runOnChanged runs BEFORE that callback is queued, so a
    // plain nextTick here would lose the race. The setTimeout puts us after it.
    // If it still misses, the user re-focuses the field and the (already
    // loaded) items are there — so never throw from this.
    openFieldMenu(fieldName: string) {
      const index = this.inputFields.findIndex(
        (f) => isEntityMaskData(f) && f.name === fieldName
      )
      // A field on another tab is hidden (v-show); opening its menu would
      // anchor a dropdown to an element that is not on screen.
      if (index === -1 || !this.isInActiveTab(index)) {
        return
      }
      this.$nextTick(() => {
        setTimeout(() => {
          const ref = this.$refs['ifield-' + index]
          const widget = Array.isArray(ref) ? ref[0] : ref
          if (widget && typeof (widget as any).menu === 'boolean') {
            ;(widget as any).menu = true
          }
        }, 0)
      })
    },

    // 'unverified' is the only status that is a problem with the address; the
    // rest are offers of a better version, so they read as information rather
    // than as something the user got wrong.
    // Return type is narrowed to VAlert's own union rather than `string`, which
    // it rejects.
    addressVerificationAlertType(ifield: IEntityMaskData): 'warning' | 'info' {
      return ifield.verificationStatus === 'unverified' ? 'warning' : 'info'
    },

    // OUTLINE icons, overriding the solid glyphs `type` would otherwise supply
    // ($info -> mdi-information, a fully-filled disc). Two reasons: the filled
    // version was the heaviest thing on a panel whose tint is 6% and whose
    // buttons are tonal, and NotificationBar.vue already uses the outline set
    // for exactly these states — so this follows the app's convention rather
    // than inventing a second one.
    addressVerificationIcon(ifield: IEntityMaskData): string {
      return ifield.verificationStatus === 'unverified'
        ? 'mdi-alert-outline'
        : 'mdi-information-outline'
    },

    // The panel's "Keep Mine" button — declining the suggestion. The spec has to
    // record WHICH address was declined, or the next blur re-verifies and puts
    // the panel straight back.
    dismissAddressVerification(ifield: IEntityMaskData) {
      const fn = `${mn} dismissAddressVerification |`
      if (!ifield.dismissVerification) {
        console.warn(`${fn} no dismissVerification on the field.`)
        return
      }
      ifield.dismissVerification(ifield, this.inputFields)
    },

    // The panel's "Use Change" button. The handler itself lives on the field spec
    // (addressFieldsCommonNoDividers.ts) so this component stays generic and
    // knows nothing about addresses — the same split as setSelectItems.
    applyAddressVerification(ifield: IEntityMaskData) {
      const fn = `${mn} applyAddressVerification |`
      if (!ifield.applyVerificationSuggestion) {
        console.warn(`${fn} no applyVerificationSuggestion on the field.`)
        return
      }
      // Returned, not awaited: this is a template click handler, and Vue routes
      // a rejected promise returned from one into its own error handler (which
      // plugins/05.vueErrorNotifier.client.ts surfaces as a notification).
      return ifield.applyVerificationSuggestion(ifield, this.inputFields)
    },

    onPersonMatchSelect(candidate: { id: string }) {
      const self = this as unknown as {
        isSubentityDialog: boolean
        $emit: (event: string, ...args: unknown[]) => void
        $router: { push: (loc: unknown) => void }
      }
      if (self.isSubentityDialog) {
        // Contact "NEW person" flow: attach the existing person to the parent
        // record (and close this sub-dialog) instead of creating a duplicate. #113
        //
        // ⚠ RETURNED, NOT `void`-ed — same reason as applyVerificationSuggestion
        // directly above. Vue routes a rejected promise returned from a template
        // handler into its own error handler, which
        // plugins/05.vueErrorNotifier.client.ts surfaces as a notification;
        // `void` opts out of that. It was `void` here, so when the hydrating GET
        // 404'd against a mock that had no /api/persons/:id route, clicking a
        // candidate produced NO visible effect at all and nothing was reported.
        return linkExistingPersonToParent(candidate.id)
      }
      // Direct person form: go to the existing person IN-APP (same tab, via the
      // router — NOT a new tab or full reload, which boot with an empty
      // sessionStorage where the user token lives and would land on login). #113
      self.$emit('cancel')
      self.$router.push({ path: '/Persons', query: { selectId: candidate.id } })
    },

    async runSetSelectItems(ifield: IEntityMaskData) {
      const fn = `${mn} runSetSelectItems |`
      if (!ifield.setSelectItems) {
        return
      }
      const that = this
      try {
        await ifield.setSelectItems(
          this.inputFields,
          this.entityParentObjectForCreate,
          ifield,
          that
        )
      } catch (e) {
        console.debug(`${fn} CAUGHT ERROR IN SET_SELECT_ITEMS. THROWING.`)
        throw e
      }
    },

    updateUnrenderedDisabled(index: number) {
      const fn = `${mn} updateUnrenderedDisabled |`
      console.debug(`${fn}`)
      this.renderUnrenderInputFields()
      this.enableDisableInputFields()
    },

    // The same setInputFieldsState pass as disabling, for `readonly`: a spec's
    // readonlyBasedOnState (CREATING / EDITING) and getStateForReadonly (a
    // function of the other fields -- e.g. the email's Sent Date / Time lock
    // once Status says the email has gone).
    setReadonlyInputFields() {
      this.setInputFieldsState({ property: 'readonly', action: 'readonly' })
    },
    // Runs at open and after every field change. It refreshes `readonly` too,
    // so a getStateForReadonly follows the fields it reads exactly as a
    // getStateForDisabled does.
    enableDisableInputFields() {
      this.setInputFieldsState({ property: 'disabled', action: 'disable' })
      this.setReadonlyInputFields()
    },
    renderUnrenderInputFields() {
      this.setInputFieldsState({
        property: 'unrendered',
        action: 'unrender',
      })
    },
    presetUnrenderedStateForEditing() {
      presetUnrenderedStatesForEditing({
        inputFields: this.inputFields,
        entityData: this.entityData,
        isEditing: this.isEditing,
        entityParentObjectForCreate: this.entityParentObjectForCreate,
        isSubentityDialog: this.isSubentityDialog,
      })
    },

    async setInputFieldsState({
      property,
      action,
    }: {
      property: string
      action: string
    }) {
      //
      function _didJustRerender({
        property,
        startingState,
        endingState,
      }: {
        property: string
        startingState: boolean
        endingState: boolean
      }) {
        return property === 'unrendered' && startingState && !endingState
      }
      function _didJustUnrender({
        property,
        startingState,
        endingState,
      }: {
        property: string
        startingState: boolean
        endingState: boolean
      }) {
        return property === 'unrendered' && !startingState && endingState
      }
      //
      const fn = `${mn} setInputFieldsState (${action}) |`
      console.debug(`${fn} running`)

      const propertyCapitalized = capitalize(property)
      const functionNameToGetStateToSetForProperty = `getStateFor${propertyCapitalized}`
      const actionCapitalized = capitalize(action)

      this.inputFields
        .filter(
          (ifield) =>
            (property === 'disabled' && isDisabledSelectItem(ifield.value)) ||
            (ifield[functionNameToGetStateToSetForProperty] !== undefined &&
              !ifield.ignoreSetStateRequests)
        )
        .forEach(async (ifield) => {
          // Can run concurrently. Does not need to be for...of.  JR.
          console.debug(`${fn} ifield = '${ifield.name}'`)
          const startingState = ifield[property]
          if (property === 'disabled' && isDisabledSelectItem(ifield.value)) {
            console.debug(
              `${fn} '${ifield.name}' ifield.value is a disabled selectItem. Setting disabled true.`
            )
            ifield.disabled = true
          } else {
            ifield[property] = ifield[functionNameToGetStateToSetForProperty](
              this.inputFields,
              this.entityData,
              this.isEditing,
              this.entityParentObjectForCreate,
              this.isSubentityDialog,
              // For a link disabled on the page that set it (contextLink.ts)
              this.$route
            )
          }
          const endingState = ifield[property]
          console.debug(
            `${fn} ${property} startingState = '${startingState}'; endingState = '${endingState}'`
          )
          if (
            typeof startingState === 'object' ||
            typeof endingState === 'object'
          )
            console.error(
              `${fn} ${property} startingState =`,
              startingState,
              `; endingState =`,
              endingState
            )
          if (ifield[property] && ifield[`clearOn${actionCapitalized}`]) {
            ifield.value = await this.getClearFieldValue({
              route: this.$route,
              ifield,
              entityParentObjectForCreate: this.entityParentObjectForCreate,
              inputFields: this.inputFields,
              property,
            })
          }
          if (
            _didJustRerender({
              property,
              startingState,
              endingState,
            }) &&
            ifield.default !== undefined
          ) {
            console.debug(
              `${fn} getting (and setting) default value for '${ifield.name}'...`
            )

            ifield.value =
              typeof ifield.default === 'function'
                ? await ifield.default({
                    route: this.$route,
                    thisField: ifield, // name swap for clarity in context of an ifield.default function definition in an inputFields spec file
                    entityParentObjectForCreate:
                      this.entityParentObjectForCreate,
                    inputFields: this.inputFields,
                  })
                : ifield.default
            console.debug(
              `${fn} got (and set) default value for '${ifield.name}':`,
              typeof ifield.value === 'object'
                ? ifield.value
                : `'${ifield.value}'`
            )
          }
        })

      const doActionBasedOnState = `${action}BasedOnState`

      this.inputFields
        .filter((ifield) => ifield[doActionBasedOnState] !== undefined)
        .forEach((ifield) => {
          console.debug(`${fn} ifield = '${ifield.name}'`)

          const potentialValueToSet =
            (ifield[doActionBasedOnState] === 'CREATING' && !this.isEditing) ||
            (ifield[doActionBasedOnState] === 'EDITING' && this.isEditing)

          ifield[property] =
            ifield[functionNameToGetStateToSetForProperty] &&
            !potentialValueToSet
              ? ifield[property]
              : potentialValueToSet

          // if (
          //   !ifield[functionNameToGetStateToSetForProperty] ||
          //   (ifield[functionNameToGetStateToSetForProperty] &&
          //     potentialValueToSet)
          // ) {
          //   ifield[property] = potentialValueToSet
          // }

          console.debug(
            `${fn} ${doActionBasedOnState} = '${ifield[doActionBasedOnState]}'.` +
              ` isEditing = ${this.isEditing}.` +
              ` isSubentityDialog = ${this.isSubentityDialog}.` +
              ` ${property} = ${ifield[property]}`
          )
        })
    },

    runOnOpen() {
      const fn = `${mn} runOnOpen |`
      console.debug(`${fn} running`)

      if (!this.isEditing) {
        console.debug(
          `${fn} Doing new entry. runOnOpen only runs in EDITING mode. So, noOp.`
        )
        return
      }
      this.inputFields
        .filter((ifield) => ifield.runOnOpen !== undefined)
        .forEach((ifield) => {
          console.debug(`${fn} ifield = '${ifield.name}'`)
          const that = this
          assertIsNotUndefined(ifield.runOnOpen) // will never be. see filter above.
          ifield.runOnOpen(ifield, that, this.entityParentObjectForCreate)
        })
    },

    setSubentityValuesFromEntity() {
      const fn = `${mn} setSubentityValuesFromEntity |`
      console.debug(`${fn} running`)

      if (this.isEditing) {
        console.debug(
          `${fn} Editing. setSubentityValuesFromEntity only runs in CREATING mode. So, noOp.`
        )
        return
      }
      if (!this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields) {
        console.debug(
          `${fn} valuesForSubentityFieldsWhoseValuesToSetFromEntityFields is falsey. Bailing.`
        )
        return
      }
      console.debug(
        `${fn} this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields:`,
        this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields
      )
      this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields?.forEach(
        (valueToSetObject) => {
          console.debug(`${fn} valueToSetObject: `, valueToSetObject)
          const ifield = this.inputFields.find(
            (ifield) => ifield.name === valueToSetObject.fieldName
          )
          assertIsDefined(ifield)
          console.debug(
            `${fn} ifield.name = '${ifield.name}'; valueToSet = `,
            valueToSetObject.value
          )
          ifield.value = valueToSetObject.value
          // ifield.ignoreSetStateRequests = true
          ifield.ignoreClearOnChangedRequests = true
        }
      )
      this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields?.forEach(
        (valueToSetObject) => {
          console.debug(
            `${fn} checking for runOnChanged for valueToSetObject: `,
            valueToSetObject
          )
          const ifield = this.inputFields.find(
            (ifield) => ifield.name === valueToSetObject.fieldName
          )
          assertIsDefined(ifield)
          console.debug(
            `${fn} ifield.name = '${ifield.name}'; valueToSet = `,
            valueToSetObject.value,
            `; value that was set = `,
            ifield.value
          )

          if (ifield.runOnChanged) {
            console.debug(`${fn} '${ifield.name}' has runOnChanged value`)
            const that = this // Sometimes need access to things like the store
            ifield.runOnChanged(
              ifield,
              this.inputFields,
              this.isSubentityDialog,
              that,
              this.entityParentObjectForCreate
            )
          }
        }
      )
    },

    setValuesForDisplayOnly() {
      const fn = `${mn} setValuesForDisplayOnly |`
      console.debug(`${fn} running`)

      if (!this.isEditing) {
        console.debug(
          `${fn} Doing new entry. setValuesForDisplayOnly only runs in EDITING mode. So, noOp.`
        )
        return
      }
      this.inputFields
        .filter((ifield) => ifield.setValueForDisplayOnly !== undefined)
        .forEach((ifield) => {
          console.debug(`${fn} ifield = '${ifield.name}'`)
          const that = this
          assertIsNotUndefined(ifield.setValueForDisplayOnly) // never will be. see filter above
          ifield.setValueForDisplayOnly({
            thisField: ifield,
            inputFields: this.inputFields,
            isSubentityDialog: this.isSubentityDialog,
            that,
          })
        })
    },

    async setDefaultValues() {
      const fn = `${mn} setDefaultValues |`
      console.debug(`${fn} Checking for fields with defaults...`)
      const filteredInputFields = this.inputFieldsOnlyIEntityMaskData.filter(
        (ifield) => ifield.default !== undefined
      )

      for (const ifield of filteredInputFields) {
        console.debug(
          `${fn} ifield.name = '${ifield.name}'; default = '${ifield.default}', `,
          ifield.default
        )

        if (
          ifield.value &&
          this.valuesForSubentityFieldsWhoseValuesToSetFromEntityFields?.some(
            (obj) => obj.fieldName === ifield.name
          )
        ) {
          console.debug(
            `${fn} ifield.start has not previously been set to something,` +
              ` but ifield.name is in array of` +
              ` valuesForSubentityFieldsWhoseValuesToSetFromEntityFields, so` +
              ` not setting ifield.value to default.`
          )
          continue
        }
        console.debug(
          `${fn} ifield.start has not previously been set to something.` +
            ` Setting ifield.value to default:`,
          ifield.default
        )
        ifield.value = await getInputFieldDefaultValue({
          route: this.$route,
          ifield,
          entityParentObjectForCreate: this.entityParentObjectForCreate,
          inputFields: this.inputFields,
          isSubentityDialog: this.isSubentityDialog,
        })

        console.debug(
          `${fn} '${ifield.name}' ifield.start set to: '${ifield.start}'. ifield.value = '${ifield.value}', `,
          ifield.value
        )

        if (ifield.runOnDefaultSet) {
          console.debug(
            `${fn} ifield '${ifield.name}' has a runOnDefaultSet function` +
              ` and we just set the default. Running it.`
          )
          await ifield.runOnDefaultSet({
            ifield,
            inputFields: this.inputFields,
            entityParentObjectForCreate: this.entityParentObjectForCreate,
          })
        }
      }
      console.debug(
        `${fn} this.inputFields after setting defaults:`,
        cloneDeep(this.inputFields)
      )
    },
    hasAValue(value: TEntityMaskDataValueTypes) {
      return hasAValue(value)
    },
    setStartingDialogTitlePrefix() {
      this.dialogTitlePrefix = this.isViewMode
        ? ''
        : this.entityNameTitlePrefixOverride ||
          this.entityNameTitlePrefixOverride === ''
        ? this.entityNameTitlePrefixOverride
        : 'New'
    },
    modifyDialogTitlePrefix(ifield: IEntityMaskData) {
      const fn = `${mn} modifyDialogTitle |`
      // currently a NoOp
      //
      // const condition = ifield.dialogTitleModifier
      //   ? `ifield.value ${ifield.dialogTitleModifier.condition}`
      //   : 'false' // This did not have quotes before typescript conversion, but eval wants a string
      // console.debug(`${fn} condition = '${condition}'`, eval(condition))

      // this.dialogTitlePrefix = eval(condition)
      //   ? ifield.dialogTitleModifier.prefix
      //   : this.entityNameTitlePrefixOverride ||
      //     this.entityNameTitlePrefixOverride === ''
      //   ? this.entityNameTitlePrefixOverride
      //   : 'New'
    },
    trySave(
      { wasClickedSaveAndAddAnother }: { wasClickedSaveAndAddAnother: boolean },
      $event: any
    ) {
      const fn = `${mn} save |`
      const actAsIfWasClickedSaveAndAddAnother =
        wasClickedSaveAndAddAnother ||
        ($event.pointerType === '' && this.allowAddAnother)
      const buttonPushed = actAsIfWasClickedSaveAndAddAnother
        ? 'SAVE AND ADD ANOTHER'
        : this.saveButtonCaptionVerbUpperCase
      console.debug(`${fn} BUTTON PUSHED: ${buttonPushed}`)
      this.saveAndAddAnotherClicked = wasClickedSaveAndAddAnother
      this.setFieldValidations()
      this.doValidation({
        callback: this.doAfterValidation,
        wasClickedSaveAndAddAnother: actAsIfWasClickedSaveAndAddAnother,
      })
    },
    cancel() {
      console.debug(`${mn} cancel | BUTTON PUSHED: CANCEL (or ESC)`)
      this.clearValidationsArray()
      this.clearShowIsValidatedCheckmark()
      this.$emit('cancel')
    },
  },
})
</script>

<style scoped>
/* Breathing room between the dialog title and the tab bar. 10px is off
   Vuetify's 4px spacing scale, hence CSS rather than an mt-* utility. */
.emf-tab-bar {
  margin-top: 10px;
}
/* V3's `.v-input__details` reserves 22px for the (usually empty) message area vs
   V2's 14px; over ~15 rows that drifts the whole form down from V2. Trim the
   reserve back to V2's 14px. This recovers ~2px/row (70->68 vs V2's 66) -- the
   rest is a floor set by the empty `.v-messages` line-height + the details
   padding-top, which we deliberately do NOT touch (zeroing padding-top lifts the
   details over the field underline and makes it vanish -- see the TableCard
   input-metrics notes). min-height ONLY. The residual ~2px/row is accepted. */
:deep(.v-input__details) {
  min-height: 14px;
}
/* Checkbox rows were the tallest single row in the form: 76px vs V2's 50.
   V2 built its 50 from a 24px control box + an 8px gap + a 14px message line;
   V3's `.v-selection-control` instead sets `min-height: 56px` and hangs a 40px
   ripple wrapper inside it, so the control alone is 56 before the details block
   is added. Pull the control floor back to V2's 32 (24 + 8) and the wrapper to
   the 24px the icon actually occupies.

   HEIGHT only -- the wrapper keeps its 40px WIDTH, because that width is what
   produces the 8px icon-to-label gap (the 24px icon centred in a 40px box). Set
   the width too and the label butts against the checkbox.

   Scoped to this form's checkboxes: `.v-selection-control` is shared by every
   checkbox, radio and switch in the app, including TableCard row checkboxes and
   the menu checkbox rows that are separately tuned in global.css. */
/* Section subtitles (entityInputFieldsService `subtitle` items) sat noticeably
   tighter to the fields above and below them than in V2.

   V2's `.v-card__subtitle` carried 16px vertical padding and took `text-h6`'s
   2rem line-height, so the box was 64px tall with the text floated 16px clear at
   both ends. V3's `.v-card-subtitle` is `padding: 0 1rem` and re-declares
   `line-height: 1.425` -- and that declaration is `.v-card .v-card-subtitle`
   (0,2,0), so it beats `.text-h6`'s non-important `1.6` -- leaving a 29px box
   with no breathing room at all.

   Restore V2's box: 16px top/bottom and the 2rem line. Left padding is left to
   the spec's own `pl-0`; right padding is already 1rem in both. Marked with a
   dedicated class rather than matched on `.text-h6`, following the
   `emf-field-divider` precedent below -- so a restyle of the utility classes
   can't silently detach this rule. */
/* The DIALOG-level subtitle (the `subtitle` prop), distinct from the per-field
   emf-field-subtitle below. Vuetify 3's .v-card-subtitle ships defaults V2's
   .v-card__subtitle never had, and here they cost both legibility and layout.
   Measured on the "Set Services to a 'Manual' Status" dialog, Bridge vs nuxt4:

     overflow  visible -> HIDDEN    flex  0 1 auto -> 0 0 auto
     opacity   1       -> 0.6       white-space  normal -> nowrap
     padding-bottom  16px -> 0      h2 overflow  -4px (fits) -> +12px (clipped)

   `overflow: hidden` is the subtle one: it establishes a block formatting
   context, so the h2's `mt-3` no longer collapses out of the box. That drops the
   text 12px INSIDE the box and the same rule then clips it — the reported "too
   low AND cut off" is one cause, not two.

   Marker class + :deep() gives (0,3,0), clearing Vuetify's (0,2,0)
   `.v-card .v-card-subtitle`; a plain scoped selector only ties and loses on
   source order. Same precedent as emf-field-subtitle. */
/* Vuetify 2 zeroed a card-text's TOP padding when it directly followed a title
   or subtitle (`.v-card__title + .v-card__text, .v-card__subtitle +
   .v-card__text { padding-top: 0 }`). Vuetify 3 dropped that adjacency rule, so
   this message block keeps the full 1rem and sits 16px lower than Bridge:
   measured subtitle-bottom -> message-top of 12px on Bridge vs 28px here, and
   the box 80px tall vs 96px. Only the MESSAGE card-text is marked — the fields
   card-text below follows another card-text, which V2's rule never covered, so
   it keeps its padding in both. */
:deep(.v-card-text.emf-dialog-message) {
  padding-top: 0;
}
/* The DIALOG TITLE. Vuetify 3's `.v-card-title` is `white-space: nowrap` +
   `overflow: hidden` + `text-overflow: ellipsis`; V2's `.v-card__title` was
   `normal`/`visible` and wrapped. So any title wider than the dialog ran off the
   edge -- measured on the create-Service-Group dialog for XXO0003, whose title
   is "New 'DIA - Boston, Lake & Waltham' Service Group": a 536px h1 inside a
   496px box in a 560px dialog, cut mid-word at "Service Grou".

   ⚠ It was NOT cut with an ellipsis, and that is the tell for where to look.
   `text-overflow` only applies to a block's OWN inline content, and the text
   here lives in a child `<h1>`; the h1 overflows visibly and the parent's
   `overflow: hidden` then chops it. So the missing ellipsis is not a second bug
   to fix -- it is evidence the overflow happens one level down, which is also
   why the pre-existing inline `word-break: break-word` did nothing: word-break
   cannot wrap what `white-space: nowrap` refuses to break. That declaration has
   moved in here.

   ⚠ `overflow` is deliberately LEFT at Vuetify's `hidden`, unlike the subtitle
   rule below. Hidden establishes a block formatting context, which is what keeps
   the h1's negative margins (`mb-n3` here, `mt-n1` on the title2 variant) from
   collapsing out of the box -- the current vertical spacing is built on that, so
   setting `visible` re-opens the exact margin-collapse trap documented for the
   subtitle and shifts the whole dialog head. Wrapping needs only `white-space`;
   the box grows in height on its own and nothing clips.

   `hyphens: manual` because Vuetify sets `auto` here: harmless while the text
   could never wrap, but once it can, Chrome will hyphenate a title mid-word.

   Marker class + :deep() gives (0,3,0), clearing Vuetify's (0,2,0)
   `.v-card .v-card-title`; a plain scoped selector only ties and loses on source
   order. Same precedent as emf-dialog-subtitle. */
:deep(.v-card-title.emf-dialog-title) {
  white-space: normal;
  word-break: break-word;
  hyphens: manual;
}
:deep(.v-card-subtitle.emf-dialog-subtitle) {
  flex: 0 1 auto;
  white-space: normal;
  overflow: visible;
  opacity: 1;
  padding-bottom: 16px;
  /* V2's .v-card__subtitle pulled itself up under the title with margin-top:
     -16px; V3's is 0, which is the whole of the extra gap. Measured: with it,
     the subtitle's top sits exactly on the h1's bottom in BOTH apps. */
  margin-top: -16px;
}
:deep(.v-card-subtitle.emf-field-subtitle) {
  padding-top: 16px;
  padding-bottom: 16px;
  line-height: 2rem;
}
:deep(.v-checkbox .v-selection-control) {
  min-height: 32px;
}
/* V3 centres the 24px checkbox glyph inside a 40px `.v-selection-control__input`
   (the ripple/touch target); V2's control box was 24px, so the glyph's left edge
   sat exactly on the field underline. Measured glyph-x minus underline-x: 0 on
   Bridge, +8 here — the reported inset. Pull the wrapper back by that half-
   difference, which moves the label with it (Bridge 628 / here 635 -> 627). */
:deep(.v-checkbox .v-selection-control__wrapper) {
  margin-inline-start: -8px;
  height: 24px;
}
/* Field-type dividers (entityInputFieldsService `divider` items) render as a
   solid coloured (purple, = `dividerColor`) rule in V2: Vuetify 2 passed the
   `color` prop straight through as the `<hr>`'s legacy `color=` HTML attribute,
   which the browser paints as a solid background bar (the "Vuetify colors not
   working for v-divider" comment refers only to the CSS *border* staying grey --
   the bar was always coloured). Vuetify 3 instead applies the colour as the
   divider's `border-color` -- already the right purple -- then fades EVERY
   `.v-divider` with `opacity: var(--v-border-opacity)` (~0.12), washing it to
   nearly invisible. Restore full opacity so the purple border shows solid, as in
   V2. Scoped to these field dividers ONLY (`emf-field-divider`) -- structural and
   menu/other dividers keep V3's default grey/faded look, per parity. */
:deep(.v-divider.emf-field-divider) {
  opacity: 1;
}
/* Field dividers and subtitles -- DARK ONLY. Both otherwise paint a white-ground
   purple (the divider `dividerColor`'s #8E24AA, the subtitle `text-primary` from
   fieldClass), which reads dim on the dark dialog. `fieldfocus` is dark mode's
   light purple, the one the theme in nuxt.config.ts settles on for thin lines
   and small text. !important on both: the divider's colour arrives as an inline
   style and the subtitle's from the !important `text-primary` utility. */
:deep(.v-theme--dark .v-divider.emf-field-divider) {
  border-color: rgb(var(--v-theme-fieldfocus)) !important;
}
/* Full opacity too: VCardSubtitle's medium-emphasis 0.7 dimmed the lighter
   colour back down (measured). */
:deep(.v-theme--dark .v-card-subtitle.emf-field-subtitle) {
  color: rgb(var(--v-theme-fieldfocus)) !important;
  opacity: 1;
}
/* A subdued message (the `doSubdueMessage` prop): regular weight, so the title
   leads, and a step lighter than the default message text. Dark: white -> 204,
   20% darker; darker still (153) read as disabled text. Light: the 87% black
   (#212121, Material grey-900) -> grey-800. The weight needs !important to beat
   the `font-weight-medium` utility. */
.emf-dialog-message--subdued > div {
  font-weight: 400 !important;
}
/* An aligned message (the `doAlignMessageWithTitle` prop): flush with the title
   and fields. The message's `mx-4` is a !important utility that set it 16px in
   from both (measured 48 vs 32). */
.emf-dialog-message--aligned > div {
  margin-left: 0 !important;
}
.v-theme--dark .emf-dialog-message--subdued > div {
  color: rgb(204, 204, 204);
}
.v-theme--light .emf-dialog-message--subdued > div {
  color: rgb(66, 66, 66);
}
/* >>> .myClass .v-select__selection,
.v-select__selection--comma,
.v-select.v-text-field input {
  color: rgb(var(--v-theme-primary-lighten-1)) !important;
} */

/* View mode hides every icon on a field EXCEPT the outer prepend one, so a
   read-only form doesn't advertise controls you cannot use -- most visibly the
   selects' dropdown arrows.

   The V2 rule was `.view-only-mode .v-input__icon:not(.v-input__icon--prepend)`,
   and `.v-input__icon` DOES NOT EXIST in Vuetify 3, so it silently matched
   nothing and every arrow came back: measured 6 of 6 arrows visible in the
   CMSS "View / Edit Specs" dialog vs 0 of 6 in Bridge. Re-expressed against V3's
   affix containers, which replace that single class:

     .v-field__append-inner   the dropdown arrow (and the clear button)
     .v-field__prepend-inner  prepend-INNER icons -- V2 hid these too, since
                              `--prepend-inner` does not match `--prepend`
     .v-input__append         append-icon

   `.v-input__prepend` (the outer prepend-icon, e.g. the date/time pickers'
   calendar and clock) is deliberately excluded, exactly as in V2. `opacity: 0`
   rather than `display: none`, also as in V2, so the affix keeps its space and
   the field's layout is unchanged. */
:deep(.view-only-mode .v-field__append-inner),
:deep(.view-only-mode .v-field__prepend-inner),
:deep(.view-only-mode .v-input__append) {
  opacity: 0;
}
/* ⚠ EXCEPT a textarea's resize handle, which still works read-only: a long
   message opened with View is exactly what someone wants taller. The exemption
   is not cosmetic -- `opacity: 0` hides an element but leaves it clickable, so
   without this the handle would be an invisible thing that still drags. */
:deep(.view-only-mode .v-field__append-inner:has(> .textarea-resize-handle)) {
  opacity: 1;
}

/* The file field uploads on a field click → hand (pointer) cursor invites it.
   The prepend icon is decorative → plain arrow (default) so it doesn't look
   clickable (overrides Vuetify's .v-icon--link pointer).

   Repointed from Bridge's V2 selectors, which the V3 markup no longer produces:
   `.v-input__slot` is now `.v-field` (with `.v-field__input` inside it, which
   does not inherit through), and `.v-input__prepend-outer` is now
   `.v-input__prepend`. Both rules had gone dead here, and measurably so --
   the field showed `auto` where Bridge shows `pointer`, and the decorative
   prepend icon showed `pointer` where Bridge shows `default`, i.e. exactly
   backwards. */
:deep(.v-file-input .v-field),
:deep(.v-file-input .v-field__input) {
  cursor: pointer;
}
:deep(.v-file-input .v-input__prepend .v-icon) {
  cursor: default;
}

/* Slider (fieldSetField's "# Columns") -- the shape V3 changed and props cannot
   reach. Sizes and colours come from the `VSlider` defaults in nuxt.config.ts;
   what is left here is geometry baked into Vuetify's own stylesheet.

   V3 deliberately draws the ACTIVE bar 2px thicker than the inactive one
   (`height: calc(var(--v-slider-track-size) + 2px)` on the track, which the fill
   inherits). V2 drew both at the same 2px with square ends, so pin the fill to
   the track size and drop V3's 6px rounding. The track is a centring flex row,
   so a 2px fill lands on the background exactly. */
:deep(.v-slider.v-input--horizontal .v-slider-track__fill) {
  height: var(--v-slider-track-size);
}
:deep(.v-slider .v-slider-track__fill),
:deep(.v-slider .v-slider-track__background) {
  border-radius: 0;
}

/* V2's thumb label was a 24px teardrop -- a square with three round corners,
   rotated 45deg, with the value counter-rotated back to upright. V3 renders a
   35x25 rounded rectangle with a separate wedge element for the pointer. Rebuild
   V2's shape: the wedge is redundant once the label itself is the pointer.
   Vuetify's own `translateX(-50%)` (which centres the label over the thumb) has
   to be restated because this transform replaces it. Only ONE of V2's two
   translateY terms carries over: V2 hung the label off `bottom: 100%` of the
   thumb and then lifted it a further 12px, where V3 anchors it at
   `bottom: calc(thumb-size / 2)`, i.e. 12px lower to begin with. Keeping both
   terms put the label 12px too high (measured); with just the -20% it sits
   28.8px above the thumb centre in both apps. Selector reaches (0,5,0) to clear
   Vuetify's
   `.v-locale--is-ltr.v-slider.v-input--horizontal .v-slider-thumb__label`
   without relying on stylesheet order. */
:deep(.v-slider.v-input--horizontal .v-slider-thumb .v-slider-thumb__label) {
  min-width: 24px;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 50% 50% 0;
  transform: translateY(-20%) translateX(-50%) rotate(45deg);
}
:deep(.v-slider .v-slider-thumb__label > div:first-child) {
  transform: rotate(-45deg);
}
:deep(.v-slider .v-slider-thumb__label-wedge) {
  display: none;
}

/* Horizontal placement. V3 moved the 8px inset that V2 put on the INNER slider
   (`.v-slider { margin: 0 8px }`, leaving the label flush with the column) out
   onto the whole input (`.v-slider.v-input--horizontal { margin-inline: 8px }`),
   which indented the label 8px past every other label in the form -- "# Columns"
   started at x=980 where its neighbours start at 972. Drop the start margin to
   put the label back on the column edge; the END margin stays 8px, which is
   exactly what V2's inner slider reserved on its right.

   Then the label-to-track gap: V3 wraps the label in `.v-input__prepend` and
   gives that a 16px end margin, so the gap came to label 12 + prepend 16 = 28px
   against V2's label 12 + slider 8 = 20px. Take the prepend to 8 and the two
   agree. Measured after both: label x=972 (aligned with "Select From" as on
   Bridge), track 1070.5..1200 against Bridge's 1069.2..1199, gap 20 in both --
   the residual 1.3px is the label text rendering that much wider in V3, not
   spacing. */
.v-slider.v-input--horizontal {
  margin-inline-start: 0;
}
.v-slider :deep(.v-input__prepend) {
  margin-inline-end: 8px;
}

/* The slider carries the only PLAIN (non-floating) input label in the form, and
   V3 arrives at its emphasis differently: it inherits a 0.87-alpha colour and
   then applies the medium-emphasis OPACITY on top, so light mode renders
   0.87 x 0.6 = 0.52 where V2 used a flat 0.6. Dropping the alpha out of the
   colour and leaving the opacity to do the work lands on V2's 0.6 exactly.
   Deliberately one rule for both themes: V3's medium-emphasis opacity is
   already 0.7 in dark and the dark colour already has no alpha, so dark
   measured correct before this and is unchanged by it. */
:deep(.v-slider .v-label) {
  color: rgb(var(--v-theme-on-surface));
}

/* The USPS address-verification panel.

   The filled surface is LOAD-BEARING, not decoration: every other message in
   this form lives inside a single field's `.v-input__details`, so proximity
   alone says which field it belongs to. This panel is a sibling of the address
   fields with no owning field, so stripped of its background it reads as a hint
   belonging to whichever field happens to sit above it. The block, plus the
   `border="start"` rule down its leading edge, is what says "this is about the
   whole address".

   Two adjustments to Vuetify's tonal defaults:

   1. A SOFTER tint. `.v-alert--variant-tonal .v-alert__underlay` paints
      `currentColor` at `--v-activated-opacity` (0.12), which is heavier than
      this needs when the panel is only advisory.
   2. READABLE text. Tonal also colours the TEXT `currentColor` -- the same hue
      as the tint it sits on -- which is what made it hard to read. Recolouring
      only `.v-alert__content` fixes that while leaving `currentColor` alone on
      the root, so the underlay and the border keep the semantic info/warning
      hue. Setting `color` on the root instead would have re-tinted both. */
.emf-address-verification {
  /* Space between the City/State/Zip underlines and the top of the block.
     MARGIN, not padding: padding-top would grow the box downward and leave its
     top edge — and so the gap — exactly where it was. */
  margin-top: 8px;

  /* The rules down both inline edges are REAL borders on the alert, deliberately
     NOT VAlert's `border` prop. That prop does not border the alert: it renders a
     separate absolutely-positioned `.v-alert__border` layer, stacked over the
     separate `.v-alert__underlay` layer that paints the tint. Two independent
     boxes that only coincide by construction — and with `border-radius: inherit`
     on both plus an 8px edge width they did not, so the rule visibly overshot
     the top of the tinted block. A real border shares the element's own box with
     the tint (the underlay covers the padding box, the borders sit just outside
     it, and neither adds vertical extent), so the two cannot disagree. */
  border-inline-start: 6px solid currentColor;
  /* Softened to sit with the 0.06 tint; at full strength it read as a hard rule.
     Set AFTER the shorthand so a browser without color-mix keeps a solid border
     rather than losing it. */
  border-inline-start-color: color-mix(in srgb, currentColor 65%, transparent);
}
/* Soften the tint on the panel's OWN underlay only.
   NOT via `--v-activated-opacity` on the alert root, which is what this used to
   do: that custom property INHERITS, and VBtn's tonal underlay reads the very
   same variable — so lowering it on the root quietly washed out the "Use Change"
   and close buttons along with the panel. Targeting the element keeps the
   buttons at Vuetify's default tonal strength, which is what makes them read as
   controls sitting ON the panel rather than dissolving into it. */
.emf-address-verification :deep(.v-alert__underlay) {
  opacity: 0.06;
}

/* Neutral text, so the message is not the type colour sitting on its own tint.
   Scoped to the TEXT block, deliberately NOT to `.v-alert__content`: the buttons
   now live inside the content too (they used to sit in the append/close grid
   areas, outside it), and `variant="tonal"` derives BOTH its text colour and its
   background tint from `currentColor` — so recolouring the whole content turned
   them neutral as well. Leaving them outside this rule keeps them inheriting the
   alert's semantic info/warning hue, which is what they had before. */
.emf-address-verification__text {
  color: rgb(var(--v-theme-on-surface));
}

/* The exact words that changed, marked by weight alone — no colour, so it does
   not compete with the semantic hue already carried by the border, icon and
   buttons, and no underline, which reads as a link.
   900 against the 500 the address block already carries (`font-weight-medium`);
   700 was tried first and did not separate enough from text that is already
   semi-bold. Set on the SPAN, so the parent's `!important` 500 is inherited and
   overridden here rather than fought. */
.emf-address-verification__changed {
  font-weight: 900;
}

/* Tighten the icon-to-text gap by 4px (Vuetify's default is 16px). Done as the
   gap itself rather than a negative margin on the text, so the text block keeps
   a clean box and nothing overlaps if the icon is ever absent. Only the text
   moves: the buttons are right-aligned in the flex row, so the extra 4px of
   content width opens on the left. */
.emf-address-verification :deep(.v-alert__prepend) {
  margin-inline-end: 12px;
}

/* Buttons a touch stronger than Vuetify's default tonal fill (0.12), so they
   read as controls sitting ON the panel rather than dissolving into its 6% tint.
   Scoped to the buttons' own underlay rather than set as `--v-activated-opacity`
   on the alert: that custom property inherits, and the panel's tint reads it
   too, so raising it there would darken the whole block along with them. */
.emf-address-verification :deep(.v-btn--variant-tonal .v-btn__underlay) {
  opacity: 0.2;
}

/* "Keep Mine" is the SECONDARY of the pair, and it recedes by going NEUTRAL
   rather than by going lighter.
   Lightening the fill alone backfired: the label stayed dark, so dropping the
   background actually RAISED its text-to-background contrast and made it shout
   louder than the primary. Hue is the lever the rest of the app already uses for
   this — the dialog's Save is `color: primary` while Cancel is `grey-lighten-4`,
   both filled, distinguished by colour and not by strength.
   So this takes the semantic hue off the button entirely: because `tonal`
   derives BOTH its tint and its label from `currentColor`, one declaration turns
   the pill grey and the text with it, leaving "Use Change" as the only coloured
   thing among the two. */
/* "Keep Mine" is the SECONDARY of the pair: same tonal treatment as "Use
   Change", backed off on BOTH fill and label.
   BOTH is the operative word, and it is what three earlier attempts got wrong.
   Lightening only the fill backfired — the label stayed dark, so a paler
   background RAISED its text-to-background contrast and it shouted louder than
   the primary. Making it neutral grey then made it the heaviest object in the
   pair, a solid grey pill against a pale teal one. Contrast, not darkness, is
   what draws the eye, so the two have to move together.
   Not knockout on the primary instead: that needs a fill dark enough to carry
   white, which the semantic teal is not and should not become. And borrowing the
   dialog's `color: primary` would give knockout at the cost of a second
   purple-knockout control on a screen that already has one — an optional
   suggestion must not compete with Save for first position.
   The alpha on `color` does double duty here: `tonal` derives its tint from
   `currentColor`, so a softer colour lightens the pill and the text in one
   declaration, and the two cannot drift apart later. */
.emf-address-verification__secondary {
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.emf-address-verification
  :deep(
    .emf-address-verification__secondary.v-btn--variant-tonal .v-btn__underlay
  ) {
  opacity: 0.08;
}

/* The label, a step lighter again than the button colour that drives the fill.
   Decoupling them is safe and deliberate: `.v-btn__underlay` is a SIBLING of
   `.v-btn__content` and takes its tint from the BUTTON's `currentColor`, so
   restyling the label here cannot move the pill.
   This also opts the secondary out of the label-darkening rule above, which
   would otherwise mix its softened grey back toward black. */
.emf-address-verification
  :deep(
    .emf-address-verification__secondary.v-btn--variant-tonal .v-btn__content
  ) {
  color: rgba(var(--v-theme-on-surface), 0.5);
}

/* Darker LABEL, without touching the fill. Applied to `.v-btn__content` rather
   than to the button itself because the tonal underlay is `background:
   currentColor` — darkening the button's own colour would drag the fill down
   with the text. The underlay is a sibling of `.v-btn__content`, so this reaches
   only the label. `currentColor` here resolves to the inherited button colour,
   i.e. the semantic info/warning hue, so this stays correct for every state. */
.emf-address-verification :deep(.v-btn--variant-tonal .v-btn__content) {
  color: color-mix(in srgb, currentColor 75%, black);
}
</style>
